import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDateTime, formatDate } from "@/lib/formatDate";
import { DocThumb } from "@/components/DocThumb";
import { DocPreviewDialog } from "@/components/DocPreviewDialog";
import { ReverseChargeDecisionDialog } from "@/components/inbox/ReverseChargeDecisionDialog";
import { CreditNoteConfirmDialog } from "@/components/inbox/CreditNoteConfirmDialog";
import { BulkGreenConfirmButton } from "@/components/inbox/BulkGreenConfirm";
import { ReverseChargeThresholdCard } from "@/components/ReverseChargeThresholdCard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStickyState, useResetStickyState } from "@/hooks/useStickyState";
import UsageBanner from "@/components/UsageBanner";
import { InboxChecksPanel } from "@/components/InboxChecksPanel";
import { InboxExtractionDetails } from "@/components/InboxExtractionDetails";
import { InboxRoutingPanel, RoutingBadge, LANE_META } from "@/components/InboxRouting";
import { InboxSplitPanel } from "@/components/InboxSplitPanel";
import { InboxUploadOptions, AccountConfidenceBadge } from "@/components/InboxUploadOptions";
import { MissingReceiptsPanel } from "@/components/MissingReceiptsPanel";
import { RelationReviewDialog } from "@/components/RelationReviewDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Upload, Inbox as InboxIcon, FileText, Receipt as ReceiptIcon,
  Landmark, RefreshCcw, Check, Trash2, Mail, Copy, Sparkles, AlertCircle, Eye, Banknote, ExternalLink, Pencil, StickyNote,
  Paperclip, ChevronDown, ChevronRight, Star, Trophy, RotateCcw, RotateCw, Crop, HeartPulse, CreditCard, ArrowRightLeft, Undo2, ZoomIn, Layers, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { useUploads, UPLOAD_DONE_EVENT } from "@/context/UploadContext";
import { ReceiptSourcesDialog } from "@/components/inbox/ReceiptSourcesDialog";
import { PayoutReportsCard } from "@/components/PayoutReportsCard";
import { DocCompareDialog } from "@/components/DocCompareDialog";
import { InboxReminderLink } from "@/components/InboxReminderLink";
import { UploadIssuesBanner } from "@/components/UploadIssuesBanner";
import { InboxCompanySuggestion } from "@/components/InboxCompanySuggestion";
import { InboxRecheckCard } from "@/components/inbox/InboxRecheckCard";
import { ReceiptReviewDialog } from "@/components/inbox/ReceiptReviewDialog";
import { ReclaimBusinessAction } from "@/components/inbox/ReclaimBusinessAction";
import { BookedPageHint } from "@/components/inbox/BookedPageHint";
import { CsvPreview, isCsvLike } from "@/components/inbox/CsvPreview";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";
import { EmailOriginalPanel } from "@/components/inbox/EmailOriginalPanel";
import { SubscriptionSeriesCard } from "@/components/inbox/SubscriptionSeriesCard";
import { InboxNoteDialog } from "@/components/inbox/InboxNoteDialog";
import { RechargeOptions } from "@/components/inbox/RechargeOptions";
import { UnknownCardBanner } from "@/components/inbox/UnknownCardBanner";
import { CardSuggestionsBanner } from "@/components/inbox/CardSuggestionsBanner";
import { InlineDateEdit, InlineAccountSelect, InlineAmountEdit } from "@/components/inbox/InboxQuickEdit";
import { WarrantyHint, CompanionHint } from "@/components/inbox/WarrantyHint";
import { Shield, FileSignature, FolderOpen, Loader2, Link2 } from "lucide-react";

const KIND_LABEL = {
  bill: { text: "Eingangsrechnung", icon: FileText, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  receipt: { text: "Beleg/Quittung", icon: ReceiptIcon, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  loan_contract: { text: "Darlehensvertrag", icon: Banknote, color: "bg-amber-50 text-amber-800 border-amber-200" },
  bonus_statement: { text: "Bonusabrechnung", icon: Trophy, color: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200" },
  insurance_benefit: { text: "Versicherungsleistung", icon: HeartPulse, color: "bg-teal-50 text-teal-800 border-teal-200" },
  warranty_certificate: { text: "Garantieschein", icon: Shield, color: "bg-violet-50 text-violet-800 border-violet-200" },
  contract: { text: "Vertrag", icon: FileSignature, color: "bg-sky-50 text-sky-800 border-sky-200" },
  document: { text: "Dokument", icon: FolderOpen, color: "bg-amber-50 text-amber-800 border-amber-200" },
  receipt_companion: { text: "Kassenbon-Beilage", icon: Paperclip, color: "bg-stone-50 text-stone-800 border-stone-300" },
};

function attachmentKindHint(name) {
  const n = (name || "").toLowerCase();
  if (/receipt|quittung|zahlungsbeleg|ricevuta|reçu/.test(n)) return "Zahlungsbeleg";
  if (/lieferschein|delivery|packing|bolla|livraison/.test(n)) return "Lieferschein";
  if (/mahnung|reminder|rappel|sollecito/.test(n)) return "Mahnung";
  if (/agb|terms|conditions/.test(n)) return "AGB";
  if (/offerte|angebot|quote|devis/.test(n)) return "Offerte";
  return null;
}


function fmtChf(v) {
  if (v == null || v === "") return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(Number(v));
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("de-CH", { year: "numeric", month: "short", day: "2-digit" });
  } catch { return v; }
}

export default function Inbox() {
  const navigate = useNavigate();
  const { company } = useCompany();
  const companyId = company?._id;
  const [items, setItems] = useState([]);
  const [trashCount, setTrashCount] = useState(0);
  const [privateCompanies, setPrivateCompanies] = useState([]);
  const [businessCompanies, setBusinessCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creditConfirm, setCreditConfirm] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightParam = searchParams.get("highlight");
  useEffect(() => {
    if (!highlightParam || !items.length) return;
    if (!items.some((x) => x.id === highlightParam)) return;
    setFocusId(highlightParam);
    const t = setTimeout(() => document.querySelector(`[data-testid="inbox-item-${highlightParam}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    return () => clearTimeout(t);
  }, [highlightParam, items]);
  const focusRef = useRef(null);
  focusRef.current = focusId;
  const actionsRef = useRef({});
  const visibleRef = useRef([]);

  const scrollToItem = (id) => setTimeout(() => document.querySelector(`[data-testid="inbox-item-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  const focusNext = (currentId) => {
    const list = visibleRef.current;
    const idx = list.findIndex((x) => x.id === currentId);
    const next = list[idx + 1] || list[idx - 1];
    if (!next) { setFocusId(null); return; }
    setFocusId(next.id);
    scrollToItem(next.id);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "Enter" && t && (t.tagName === "BUTTON" || t.tagName === "A" || t.getAttribute?.("role") === "button")) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"]')) return;
      const list = visibleRef.current.filter((x) => x.status === "pending");
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!list.length) return;
        e.preventDefault();
        const idx = list.findIndex((x) => x.id === focusRef.current);
        const next = e.key === "ArrowDown" ? list[idx < 0 ? 0 : Math.min(idx + 1, list.length - 1)] : list[idx <= 0 ? 0 : idx - 1];
        setFocusId(next.id);
        scrollToItem(next.id);
      } else if (e.key === "Enter") {
        const cur = focusRef.current;
        if (!cur) return;
        const btn = document.querySelector(`[data-testid="inbox-item-${cur}"] [data-testid^="inbox-post-"], [data-testid="inbox-item-${cur}"] [data-testid^="inbox-file-"]`);
        if (btn && !btn.disabled) { e.preventDefault(); btn.click(); }
      } else if ((e.key === "b" || e.key === "B" || e.key === "n" || e.key === "N") && focusRef.current) {
        const it = visibleRef.current.find((x) => x.id === focusRef.current);
        if (!it || it.status !== "pending") return;
        e.preventDefault();
        if (e.key.toLowerCase() === "b") { if (it.kind !== "bonus_statement") actionsRef.current.edit?.(it); }
        else actionsRef.current.note?.(it);
      } else if (e.key === "Escape") {
        setFocusId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const { enqueue, active: activeUploads } = useUploads();
  const uploading = activeUploads.some((u) => u.companyId === companyId);
  const [uploadOpts, setUploadOpts] = useState(() => ({ purpose: "", note: new URLSearchParams(window.location.search).get("hint") || "", paid_by_employee: false, employee_id: "" }));
  const [mailConfig, setMailConfig] = useState(null);
  const [showMailDialog, setShowMailDialog] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [myCompanies, setMyCompanies] = useState([]);
  const [moveItem, setMoveItem] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    apiClient.get("/my/companies").then((r) => setMyCompanies((r.data || []).filter((c) => !c.archived))).catch(() => setMyCompanies([]));
  }, []);
  const [moveRecharge, setMoveRecharge] = useState("none");
  const moveToCompany = async () => {
    if (!moveItem || !moveTarget) return;
    setMoving(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${moveItem.id}/move-company`, { target_company_id: moveTarget, recharge: moveRecharge });
      const rc = r.data.recharge;
      if (rc?.mode === "invoice") toast.success(`Rechnung ${rc.invoice_number} an ${r.data.target_company_name} erstellt und gebucht – dort als Eingangsrechnung erfasst. Beleg bleibt hier.`, { duration: 7000 });
      else if (rc?.mode === "kontokorrent") toast.success(`Beleg an ${r.data.target_company_name} übertragen – offener Beleg auf Kontokorrent 1180 hier angelegt`, { duration: 6000 });
      else toast.success(`Beleg an ${r.data.target_company_name || "andere Firma"} übertragen`);
      setMoveItem(null);
      setMoveRecharge("none");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übertragung fehlgeschlagen");
    } finally {
      setMoving(false);
    }
  };
  const [undoingId, setUndoingId] = useState(null);
  const undoMove = async (it) => {
    const back = it.source_meta?.moved_from_company_id;
    if (!back) return;
    setUndoingId(it.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/move-company`, { target_company_id: back });
      toast.success(`Beleg zurück bei ${r.data.target_company_name || "Ursprungsfirma"}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rückübertragung fehlgeschlagen");
    } finally {
      setUndoingId(null);
    }
  };
  const [editItem, setEditItem] = useState(null);
  const [review, setReview] = useState(null);
  const [bookedPreview, setBookedPreview] = useState(null);
  const [rcDecision, setRcDecision] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [showOriginalEmail, setShowOriginalEmail] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const downloadPreview = async () => {
    if (!previewBlobUrl) return;
    try {
      const blob = await (await fetch(previewBlobUrl)).blob();
      const name = previewPages[previewPage]?.file_name || previewItem?.file_name || "beleg";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
    } catch { toast.error("Download fehlgeschlagen"); }
  };
  const [compare, setCompare] = useState(null);
  const [accountEditItem, setAccountEditItem] = useState(null);
  const [noteEditItem, setNoteEditItem] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState("");
  const [saveRuleInbox, setSaveRuleInbox] = useState(false);
  const [keywordRule, setKeywordRule] = useState({ enabled: false, keywords: "" });
  const [kindFilter, setKindFilter] = useStickyState("inbox.kindFilter", "all", companyId);
  const [expandedAtts, setExpandedAtts] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const resetInboxView = useResetStickyState("inbox", companyId);
  const fileInputRef = useRef(null);

  const loadedKeyRef = useRef(null);
  const refresh = useCallback(async () => {
    if (!companyId) return;
    const key = `${companyId}:${kindFilter}`;
    const initial = loadedKeyRef.current !== key;
    if (initial) setLoading(true);
    try {
      const st = kindFilter === "trash" ? "discarded" : "pending";
      const r = await apiClient.get(`/companies/${companyId}/inbox?status=${st}`);
      setItems(r.data.items || []);
      setPrivateCompanies(r.data.private_companies || []);
      setBusinessCompanies(r.data.company_type === "privat" ? (r.data.business_companies || []) : []);
      loadedKeyRef.current = key;
      apiClient.get(`/companies/${companyId}/inbox/stats`).then((x) => setTrashCount(x.data?.discarded_total || 0)).catch(() => {});
      window.dispatchEvent(new Event("inbox:changed"));
    } catch (e) {
      toast.error("Posteingang konnte nicht geladen werden");
    } finally {
      if (initial) setLoading(false);
    }
  }, [companyId, kindFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onDone = (e) => { if (e.detail?.companyId === companyId) refresh(); };
    window.addEventListener(UPLOAD_DONE_EVENT, onDone);
    return () => window.removeEventListener(UPLOAD_DONE_EVENT, onDone);
  }, [companyId, refresh]);

  const handleFiles = (fileList) => {
    if (!fileList?.length) return;
    if (uploadOpts.paid_by_employee && !uploadOpts.employee_id) {
      toast.error("Bitte den Mitarbeitenden wählen, der privat bezahlt hat.");
      return;
    }
    enqueue(companyId, fileList, uploadOpts);
    setUploadOpts({ purpose: "", note: "", paid_by_employee: false, employee_id: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const rejectItem = async (it) => {
    const reason = window.prompt("Grund der Ablehnung (sieht der Mitarbeiter im Portal):", "Kein Geschäftsaufwand");
    if (!reason) return;
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/reject`, { reason });
      toast.success("Beleg abgelehnt – Mitarbeiter wurde informiert");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnen fehlgeschlagen");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const flipKind = async (it) => {
    const newKind = it.kind === "bill" ? "receipt" : it.kind === "receipt" ? "bill" : "receipt";
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/categorize`, { kind: newKind });
      toast.success(`Umkategorisiert zu ${KIND_LABEL[newKind].text}`);
      await refresh();
    } catch (e) {
      toast.error("Fehler beim Umkategorisieren");
    }
  };

  const toggleWarranty = async (it, create) => {
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/warranty-flag`, { create });
      toast.success(create ? "Beim Bestätigen wird zusätzlich eine Garantie angelegt" : "Garantie wird nicht angelegt");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Markieren fehlgeschlagen"); }
  };

  const setAttachCert = (it, checked) => setItems((l) => l.map((x) => (x.id === it.id ? { ...x, _attach_cert: checked } : x)));

  const fileAsPurchase = async (it) => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/to-purchase`, { target_item_id: it.purchase_receipt?.id || null });
      toast.success(r.data?.message || "Zum Kaufbeleg gelegt");
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Zuordnen fehlgeschlagen"); return false; }
  };

  const fileAsWarranty = async (it) => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/to-warranty`, { attach_to_receipt: it._attach_cert !== false });
      toast.success(r.data?.message || "Im Garantiebereich abgelegt");
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Ablegen fehlgeschlagen"); return false; }
  };

  const fileAsDocument = async (it) => {
    const suggested = it.document?.folder || "Sonstiges";
    let folders = ["Protokolle", "Behörden", "Versicherung", "Personal", "Zertifikate", "Sonstiges"];
    try { const r = await apiClient.get(`/companies/${companyId}/documents/folders`); folders = (r.data?.folders || []).map((f) => f.name); } catch { /* defaults */ }
    const choice = window.prompt(`In welchen Dokumentenordner ablegen?\n${folders.map((f, i) => `${i + 1} = ${f}`).join("\n")}\n\nNummer oder neuen Ordnernamen eingeben:`, suggested);
    if (choice === null) return false;
    const idx = parseInt(choice, 10);
    const folder = Number.isFinite(idx) && folders[idx - 1] ? folders[idx - 1] : (choice.trim() || suggested);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/to-document`, { folder });
      toast.success(r.data?.message || "Im Dokumentenordner abgelegt");
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Ablegen fehlgeschlagen"); return false; }
  };

  const fileAsContract = async (it) => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/to-contract`);
      toast.success(r.data?.message || "Im Verträge-Ordner abgelegt");
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Ablegen fehlgeschlagen"); return false; }
  };

  const toggleCreditNote = async (it) => {
    const next = !it.extracted?.is_credit_note;
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/credit-note`, { is_credit_note: next });
      toast.success(next ? "Als Gutschrift markiert – wird beim Bestätigen nicht als Aufwand gebucht" : "Gutschrift-Markierung aufgehoben");
      if (r.data?.warning) toast.warning(r.data.warning, { duration: 8000 });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Markieren fehlgeschlagen");
    }
  };

  const setCreditOrigin = async (it, originalId) => {
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/credit-note`, { is_credit_note: true, original_receipt_id: originalId });
      toast.success(originalId ? "Mit Kaufbeleg verknüpft – gleiches Konto wie der Kauf" : "Verknüpfung entfernt");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verknüpfen fehlgeschlagen");
    }
  };

  const discard = async (it, skipConfirm = false, onUndo = null) => {
    if (!skipConfirm && !window.confirm("Dieses Dokument verwerfen?")) return false;
    try {
      await apiClient.delete(`/companies/${companyId}/inbox/${it.id}`);
      toast.success(`«${it.extracted?.supplier_name || it.file_name}» verworfen`, {
        duration: 8000,
        description: "Im Papierkorb wiederherstellbar",
        action: { label: "Rückgängig", onClick: async () => {
          try {
            await apiClient.post(`/companies/${companyId}/inbox/${it.id}/restore`);
            toast.success("Wiederhergestellt – Beleg ist zurück im Posteingang");
            onUndo?.(it);
            await refresh();
          } catch (e) { toast.error(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen"); }
        } },
      });
      await refresh();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Verwerfen fehlgeschlagen"); return false; }
  };

  const postItem = async (it, override = {}) => {
    const rcCheck = (it.checks?.checks || []).find((c) => c.id === "reverse_charge" && c.requires_decision);
    if (rcCheck && override.reverse_charge === undefined && it.extracted?.reverse_charge == null) {
      setRcDecision({ item: it, override, info: { status: rcCheck.label.includes("Konflikt") ? "conflict" : "candidate", suggested: rcCheck.suggested, reasons: [rcCheck.detail], threshold_hint: rcCheck.threshold_hint } });
      return false;
    }
    if (it.extracted?.is_credit_note && !override._creditConfirmed) {
      setCreditConfirm({ item: it, override });
      return false;
    }
    const { _creditConfirmed, ...rest } = override;
    override = rest;
    if (it.duplicate_warning && !override.force) {
      const others = (it.duplicate_warning.others || []).slice(0, 2);
      if (others.length) {
        // Beide Dokumente sofort nebeneinander zeigen – Entscheidung «nicht gleich → bestätigen» oder «gleich → verwerfen»
        return new Promise((resolve) => setCompare({ it, other: others[0], others, confirm: { override, resolve } }));
      }
      const names = (it.duplicate_warning.others || []).map((o) => `«${o.file_name}»`).join(", ");
      const msg = it.duplicate_warning.already_booked
        ? `🛑 Bereits verbucht!\n\nDieses Dokument hat ${it.duplicate_warning.reason} wie ${names}, das bereits bestätigt & verbucht wurde.\n\nWenn du es nochmals bestätigst, wird der Aufwand DOPPELT gebucht.\n\nTrotzdem bestätigen?`
        : `⚠️ Mögliches Duplikat!\n\nDieses Dokument hat ${it.duplicate_warning.reason} wie ${names}.\n\nWenn du BEIDE bestätigst, wird der Aufwand doppelt gebucht.\n\nTrotzdem bestätigen?`;
      const ok = window.confirm(msg);
      if (!ok) return false;
      override = { ...override, force: true };
    }
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/post`, override);
      if (r.data?.warning && typeof r.data.warning === "string") toast.warning(r.data.warning, { duration: 9000 });
      if (r.data.kind === "bonus_statement" || r.data.kind === "insurance_benefit") {
        toast.success(r.data.message || "Erledigt");
        await refresh();
        return true;
      }
      const target = r.data.kind === "bill" ? "Eingangsrechnung" : "Beleg";
      toast.success(`${target} erstellt — bereit zum Buchen${r.data.reverse_charge ? " · als Bezugsteuer markiert (Art. 45 MWSTG, Lieferanten-Regel)" : ""}`, { duration: r.data.reverse_charge ? 7000 : 4000 });
      if (r.data.bank_refund) toast.success(`Mit Bank-Gutschrift +${Number(r.data.bank_refund.amount).toFixed(2)} vom ${fmtDate(r.data.bank_refund.date)} verknüpft – Bankabgleich erledigt`, { duration: 7000 });
      focusNext(it.id);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      await refresh();
      setEditItem(null);
      return true;
    } catch (e) {
      if (e.response?.status === 428 && e.response.data?.detail?.code === "reverse_charge_decision_required") {
        setRcDecision({ item: it, override, info: e.response.data.detail });
        return false;
      }
      if (e.response?.status === 409 && !override.force) {
        const ok = window.confirm(`🛑 ${e.response.data?.detail}\n\nTrotzdem buchen (Aufwand wird DOPPELT erfasst)?`);
        if (ok) return postItem(it, { ...override, force: true });
        return false;
      }
      toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
      return false;
    }
  };

  const postAsLoan = async (it) => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/post-as-loan`, {});
      toast.success("Darlehen erstellt — bereit für Auszahlung");
      await refresh();
      navigate(`/loans/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Darlehens-Konvertierung fehlgeschlagen — bitte ergänze Felder manuell");
    }
  };

  const [previewPage, setPreviewPage] = useState(0);
  const previewPages = previewItem ? [{ file_id: null, file_name: previewItem.base_file_name || previewItem.file_name, content_type: previewItem.base_content_type || previewItem.content_type }, ...(previewItem.attachments || [])] : [];
  const showPreviewPage = async (it, idx) => {
    const pages = [{ file_id: null, file_name: it.file_name, content_type: it.content_type }, ...(it.attachments || [])];
    const pg = pages[idx];
    if (!pg) return;
    setPreviewPage(idx);
    setPreviewBlobUrl(null);
    try {
      const url = pg.file_id ? `/companies/${companyId}/inbox/${it.id}/file?attachment_id=${pg.file_id}` : `/companies/${companyId}/inbox/${it.id}/file`;
      const r = await apiClient.get(url, { responseType: "blob" });
      setPreviewBlobUrl(URL.createObjectURL(new Blob([r.data], { type: pg.content_type || guessType(pg.file_name) })));
      setPreviewItem((p) => (p ? { ...p, file_name: pg.file_name, content_type: pg.content_type, attachment_preview: !!pg.file_id } : p));
    } catch (e) { toast.error("Seite konnte nicht geladen werden"); }
  };

  const openPreview = async (it) => {
    setPreviewItem({ ...it, base_file_name: it.file_name, base_content_type: it.content_type });
    setPreviewPage(0);
    setPreviewBlobUrl(null);
    try {
      const r = await apiClient.get(`/companies/${companyId}/inbox/${it.id}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: it.content_type || "application/octet-stream" });
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Vorschau konnte nicht geladen werden");
      setPreviewItem(null);
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewItem(null);
    setShowOriginalEmail(false);
  };

  const uncropPreview = async () => {
    if (!previewItem || rotating) return;
    setRotating(true);
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${previewItem.id}/uncrop`);
      const r = await apiClient.get(`/companies/${companyId}/inbox/${previewItem.id}/file`, { responseType: "blob" });
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(URL.createObjectURL(new Blob([r.data], { type: previewItem.content_type || "image/jpeg" })));
      setPreviewItem((p) => ({ ...p, auto_cropped: false }));
      setItems((prev) => prev.map((x) => (x.id === previewItem.id ? { ...x, auto_cropped: false } : x)));
      toast.success("Original wiederhergestellt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Original konnte nicht wiederhergestellt werden");
    } finally {
      setRotating(false);
    }
  };

  const [rotating, setRotating] = useState(false);
  const rotatePreview = async (degrees) => {
    if (!previewItem || rotating) return;
    setRotating(true);
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${previewItem.id}/rotate`, { degrees });
      const r = await apiClient.get(`/companies/${companyId}/inbox/${previewItem.id}/file`, { responseType: "blob" });
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(URL.createObjectURL(new Blob([r.data], { type: previewItem.content_type || "image/jpeg" })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bild konnte nicht gedreht werden");
    } finally {
      setRotating(false);
    }
  };

  // Iter283: E-Mail-Bundle — Beilagen anzeigen / verwalten
  const toggleAtts = (id) => setExpandedAtts((p) => ({ ...p, [id]: !p[id] }));

  const previewAttachment = async (it, a) => {
    setPreviewItem({ ...it, base_file_name: it.file_name, base_content_type: it.content_type, file_name: a.file_name, content_type: a.content_type, attachment_preview: true });
    setPreviewPage(Math.max(0, (it.attachments || []).findIndex((x) => x.file_id === a.file_id) + 1));
    setPreviewBlobUrl(null);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/inbox/${it.id}/file?attachment_id=${a.file_id}`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: a.content_type || guessType(a.file_name) });
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Vorschau konnte nicht geladen werden");
      setPreviewItem(null);
    }
  };

  const moveAttachment = async (it, a, dir) => {
    const ids = (it.attachments || []).map((x) => x.file_id);
    const i = ids.indexOf(a.file_id); const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await apiClient.put(`/companies/${companyId}/inbox/${it.id}/attachments/order`, { order: ids });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, attachments: ids.map((id) => x.attachments.find((y) => y.file_id === id)) } : x)));
      toast.success(`Reihenfolge angepasst – «${a.file_name}» ist jetzt Seite ${j + 2}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reihenfolge konnte nicht gespeichert werden"); }
  };

  // Iter331: Reihenfolge direkt in der Vorschau ändern (aktuelle Seite nach vorne/hinten, Seite 1 = Hauptdokument)
  const movePreviewPage = async (dir) => {
    if (!previewItem) return;
    const it = previewItem;
    const atts = it.attachments || [];
    const idx = previewPage;
    const target = idx + dir;
    if (target < 0 || target > atts.length) return;
    try {
      if (idx === 0 || target === 0) {
        // Wechsel des Hauptdokuments: Seite 2 wird Seite 1 (altes Hauptdokument wird Seite 2)
        const a = idx === 0 ? atts[0] : atts[idx - 1];
        await apiClient.post(`/companies/${companyId}/inbox/${it.id}/attachments/${a.file_id}/promote`);
      } else {
        const ids = atts.map((x) => x.file_id);
        [ids[idx - 1], ids[target - 1]] = [ids[target - 1], ids[idx - 1]];
        await apiClient.put(`/companies/${companyId}/inbox/${it.id}/attachments/order`, { order: ids });
      }
      const r = await apiClient.get(`/companies/${companyId}/inbox?status=pending`);
      const list = r.data.items || [];
      setItems(list);
      const fresh = list.find((x) => x.id === it.id);
      if (fresh) {
        setPreviewItem({ ...fresh, base_file_name: fresh.file_name, base_content_type: fresh.content_type });
        await showPreviewPage(fresh, target);
      }
      toast.success(`Reihenfolge geändert – diese Seite ist jetzt Seite ${target + 1}${target === 0 ? " (Hauptdokument)" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reihenfolge konnte nicht geändert werden"); }
  };

  const promoteAttachment = async (it, a) => {
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/attachments/${a.file_id}/promote`);
      toast.success(`«${a.file_name}» ist jetzt das Hauptdokument — dieses wird verbucht`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hauptdokument konnte nicht gewechselt werden");
    }
  };

  const detachAttachment = async (it, a) => {
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/attachments/${a.file_id}/detach`);
      toast.success(`«${a.file_name}» entfernt – liegt wieder als eigenes Dokument im Posteingang`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    }
  };

  // Iter284: Zwei Einträge per Drag & Drop manuell bündeln
  const bundleItems = async (sourceId, targetId) => {
    const source = items.find((i) => i.id === sourceId);
    const target = items.find((i) => i.id === targetId);
    if (!source || !target) return;
    if (!window.confirm(`«${source.file_name}» als Beilage zu «${target.file_name}» bündeln?\n\nNur das Hauptdokument wird verbucht.`)) return;
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${targetId}/bundle`, { source_item_id: sourceId });
      toast.success(`Gebündelt — «${source.file_name}» ist jetzt Beilage von «${target.file_name}»`);
      setExpandedAtts((p) => ({ ...p, [targetId]: true }));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bündeln fehlgeschlagen");
    }
  };

  const guessType = (name) =>
    String(name || "").toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg";

  const openCompare = (it) => {
    const others = (it.duplicate_warning?.others || []).slice(0, 2);
    if (others.length) setCompare({ it, other: others[0], others });
  };

  // Iter332: gehört zu einem bereits verbuchten Beleg → als Seite anhängen (vorne = Seite 1, hinten = letzte Seite)
  const attachToBooked = async (position) => {
    if (!compare) return;
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${compare.it.id}/attach-to-confirmed`, { target_item_id: compare.other.id, position });
      toast.success(`«${compare.it.file_name}» als ${position === "before" ? "Seite 1" : `Seite ${r.data.pages.length}`} an den verbuchten ${r.data.target_type === "bill" ? "Rechnung" : "Beleg"} angehängt – keine neue Buchung`);
      closeCompare();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Anhängen fehlgeschlagen"); }
  };

  // Iter329: kein Duplikat, sondern Seite 1+2 → als ein Dokument mit Beilage(n) zusammenführen
  const mergeCompare = async () => {
    if (!compare) return;
    const ids = [compare.it.id, ...compare.others.filter((o) => !compare.it.duplicate_warning?.already_booked).map((o) => o.id)];
    if (ids.length < 2) { toast.error("Das Gegenstück ist bereits verbucht – zusammenführen nicht möglich"); return; }
    try {
      await apiClient.post(`/companies/${companyId}/inbox/relation-groups/resolve`, { item_ids: ids, action: "merge", main_id: compare.it.id });
      toast.success(`${ids.length} Dokumente zusammengeführt – «${compare.it.file_name}» ist das Hauptdokument, die anderen sind Beilagen`);
      closeCompare();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Zusammenführen fehlgeschlagen"); }
  };

  const closeCompare = () => { compare?.confirm?.resolve?.(false); setCompare(null); };

  const compareFacts = (c) => {
    if (!c) return null;
    const ex = c.it.extracted || {};
    const docsF = [
      { head: "Dieses Dokument", supplier: ex.supplier_name, amount: ex.total_amount, currency: ex.currency, date: ex.invoice_date, no: ex.invoice_number },
      ...c.others.map((o) => ({ head: c.it.duplicate_warning?.already_booked ? "Bereits verbucht" : "Gegenstück", supplier: o.supplier, amount: o.amount, currency: o.currency, date: o.date, no: o.invoice_number })),
    ];
    const money = (d) => (d.amount != null ? `${d.currency || "CHF"} ${Number(d.amount).toFixed(2)}` : "");
    const fmtD = (d) => (d.date ? formatDate(d.date) : "");
    const normNo = (v) => String(v || "").replace(/[\s\-/.]/g, "").toLowerCase();
    const normS = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
    const row = (key, label, get, norm = (v) => v) => {
      const vals = docsF.map(get);
      const base = norm(vals[0]);
      return { key, label, heads: docsF.map((d) => d.head), values: vals, diff: vals.some((v) => norm(v) !== base) };
    };
    return [
      row("amount", "Betrag", (d) => money(d), (v) => v),
      row("date", "Datum", (d) => fmtD(d), (v) => v),
      row("invoice_number", "Rechnungs-/Bon-Nr.", (d) => d.no || "", normNo),
      row("supplier", "Lieferant", (d) => d.supplier || "", normS),
    ];
  };

  const confirmCompareNotEqual = async () => {
    if (!compare) return;
    const c = compare;
    setCompare(null);
    const ok = await postItem(c.it, { ...(c.confirm?.override || {}), force: true });
    c.confirm?.resolve?.(ok);
  };

  const suggestKeywords = (it) => {
    const src = `${it?.source_meta?.user_note || ""} ${it?.extracted?.supplier_name || ""}`.toLowerCase();
    const stop = new Set(["für", "und", "mit", "der", "die", "das", "den", "dem", "ein", "eine", "von", "im", "am", "zu", "auf", "ag", "gmbh", "sa"]);
    return Array.from(new Set(src.replace(/[^a-zäöüéèà0-9\s-]/g, " ").split(/\s+/).filter((w) => w.length > 3 && !stop.has(w)))).slice(0, 4).join(", ");
  };

  const openAccountEdit = async (it) => {
    setAccountFilter("");
    setSaveRuleInbox(false);
    setKeywordRule({ enabled: false, keywords: suggestKeywords(it) });
    setAccountEditItem(it);
    await ensureAccounts();
  };

  const ensureAccounts = useCallback(async () => {
    if (accounts.length) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/accounts`);
      setAccounts((r.data || []).sort((a, b) => String(a.code).localeCompare(String(b.code))));
    } catch (e) {
      toast.error("Kontenplan konnte nicht geladen werden");
    }
  }, [accounts.length, companyId]);

  const saveAccount = async (acc) => {
    try {
      const kws = keywordRule.enabled ? keywordRule.keywords.split(",").map((k) => k.trim()).filter(Boolean) : [];
      const r = await apiClient.post(`/companies/${companyId}/inbox/${accountEditItem.id}/account`, {
        account: `${acc.code} ${acc.name}`,
        keyword_rule: kws.length ? { label: `${acc.name} (gelernt)`, keywords: kws } : null,
      });
      toast.success(`Konto angepasst: ${acc.code} ${acc.name}`);
      if (r.data?.keyword_rule) toast.success(`Stichwortregel «${r.data.keyword_rule.keywords.join(", ")}» → ${acc.code} angelegt – gilt ab sofort für neue Belege`);
      setKeywordRule({ enabled: false, keywords: "" });
      if (saveRuleInbox) {
        const vendor = accountEditItem?.extracted?.supplier_name;
        if (vendor) {
          try {
            await apiClient.post(`/companies/${companyId}/bank/booking-rules`, {
              vendor_name: vendor,
              account_id: String(acc.code),
            });
            toast.success(`📌 Regel gespeichert: «${vendor}» → Konto ${acc.code} (jederzeit änderbar)`);
          } catch (err) {
            toast.error("Regel konnte nicht gespeichert werden");
          }
        } else {
          toast.warning("Keine Gegenpartei erkannt — Regel nicht möglich");
        }
      }
      if (r.data?.warning) {
        const w = r.data.warning;
        toast.warning(
          `KI sieht das anders: ${w.hint || "Konto passt evtl. nicht zum Beleg."}${w.better ? ` Vorschlag: ${w.better}` : ""}`,
          { duration: 9000 },
        );
      }
      if (r.data?.learning) {
        toast.info("KI lernt aus deiner Korrektur und verfeinert die Konto-Beschreibung.", { duration: 5000 });
      }
      setAccountEditItem(null);
      refresh();
    } catch (e) {
      toast.error("Konto konnte nicht gespeichert werden");
    }
  };

  const applyNoteSimilar = async (ids, recheck) => {
    const r = await apiClient.post(`/companies/${companyId}/inbox/${noteEditItem.id}/note/apply-similar`, { note: noteText, item_ids: ids, recheck });
    const { count, failed, credit_note } = r.data;
    toast.success(`${recheck ? "Notiz & KI-Prüfung" : "Notiz"} auf ${count} Belege übernommen${credit_note ? " – alle als Gutschrift markiert" : ""}`, { duration: 6000 });
    if (failed?.length) toast.warning(`${failed.length} Belege konnten nicht aktualisiert werden`);
    setNoteEditItem(null);
    await refresh();
  };

  const saveNote = async (similarIds = []) => {
    if (!noteEditItem) return;
    setNoteSaving(true);
    try {
      if (similarIds.length) { await applyNoteSimilar(similarIds, false); return; }
      const r = await apiClient.post(`/companies/${companyId}/inbox/${noteEditItem.id}/note`, {
        note: noteText,
      });
      toast.success(noteText.trim() ? "Notiz gespeichert" : "Notiz entfernt");
      if (r.data?.expense_claim) {
        toast.success(`Als Spesen von ${r.data.expense_claim.employee_name} markiert – Buchung Aufwand an 2060, Rückerstattung im nächsten Lohnlauf`, { duration: 7000 });
      } else if (r.data?.employee_hint) {
        toast.warning(r.data.employee_hint, { duration: 8000 });
      }
      if (r.data?.new_suggestion) {
        toast.info(`KI-Konto-Vorschlag anhand deiner Notiz aktualisiert: ${r.data.new_suggestion}`, { duration: 6000 });
      }
      setNoteEditItem(null);
      refresh();
    } catch (e) {
      toast.error("Notiz konnte nicht gespeichert werden");
    } finally {
      setNoteSaving(false);
    }
  };

  // Neuprüfung: Notiz (optional neu) + gesamter Belegtext → KI vergleicht altes/neues Konto
  const [recheckingId, setRecheckingId] = useState(null);
  const recheckItem = async (it, note = undefined) => {
    setRecheckingId(it.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/recheck`, note === undefined ? {} : { note });
      const rc = r.data;
      if (rc.changed || rc.new_account) toast.success("Neuprüfung fertig – Vergleich am Beleg prüfen und übernehmen", { duration: 5000 });
      else if (rc.suggested_account) toast.info(`Neuprüfung: Konto ${rc.suggested_account} bleibt passend`, { duration: 5000 });
      else toast.warning("Neuprüfung: kein passendes Konto gefunden – Erklärung am Beleg lesen");
      setNoteEditItem(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neuprüfung fehlgeschlagen");
    } finally {
      setRecheckingId(null);
    }
  };
  const saveNoteAndRecheck = async (similarIds = []) => {
    if (!noteEditItem) return;
    setNoteSaving(true);
    try {
      if (similarIds.length) await applyNoteSimilar(similarIds, true);
      else await recheckItem(noteEditItem, noteText);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally { setNoteSaving(false); }
  };

  const openNoteEdit = (it) => {
    setNoteText(it.source_meta?.user_note || "");
    setNoteEditItem(it);
  };
  actionsRef.current = { edit: setEditItem, note: openNoteEdit };

  const [reprocessingId, setReprocessingId] = useState(null);
  const [rerenderingId, setRerenderingId] = useState(null);
  const rerenderEmail = async (it) => {
    setRerenderingId(it.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/rerender-email`);
      toast.success(`«${it.file_name}» im Original-Layout neu erzeugt${r.data.source === "imap" ? " (Mail aus Postfach nachgeladen)" : ""}`);
      await refresh();
      if (previewItem?.id === it.id) openPreview(it);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neu-Rendern fehlgeschlagen");
    } finally {
      setRerenderingId(null);
    }
  };
  const [payoutReload, setPayoutReload] = useState(0);
  const reprocessPayout = async (it) => {
    setReprocessingId(it.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${it.id}/reprocess-payout`);
      const d = r.data;
      toast.success(`${d.provider}-Auszahlung ${d.payout_date || ""}: Brutto CHF ${Number(d.gross || 0).toFixed(2)} − Gebühren CHF ${Number(d.fees || 0).toFixed(2)} = überwiesen CHF ${Number(d.net || 0).toFixed(2)}`, { duration: 8000 });
      setPayoutReload((n) => n + 1);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verarbeitung fehlgeschlagen");
    } finally {
      setReprocessingId(null);
    }
  };

  const loadMailConfig = async () => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/inbox/mail-config`);
      setMailConfig(r.data);
      setShowMailDialog(true);
    } catch (e) { toast.error("Mail-Konfiguration nicht verfügbar"); }
  };

  const stats = {
    total: items.length,
    bills: items.filter(i => i.kind === "bill").length,
    receipts: items.filter(i => i.kind === "receipt").length,
  };

  const queueItems = items
    .filter((i) => i.status === "pending" && i.kind !== "bonus_statement")
    .sort((a, b) => ((LANE_META[a.routing?.lane] || LANE_META.review).order - (LANE_META[b.routing?.lane] || LANE_META.review).order));
  const visibleItems = kindFilter === "all" || kindFilter === "trash"
    ? items
    : kindFilter === "queue"
    ? queueItems
    : items.filter(i => i.kind === kindFilter);
  const reviewItems = visibleItems.filter((i) => i.status === "pending" && i.kind !== "bonus_statement" && i.file_name);
  const reviewLive = useMemo(() => {
    if (!review) return [];
    const ids = new Set(review.items.map((r) => r.id));
    return items.filter((i) => ids.has(i.id));
  }, [review, items]);
  visibleRef.current = visibleItems;
  const openReview = (startId) => {
    const i = startId ? reviewItems.findIndex((x) => x.id === startId) : 0;
    setReview({ items: reviewItems, startIndex: Math.max(0, i) });
  };
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const reanalyze = async (it) => {
    setReanalyzingId(it.id);
    try {
      const r = (await apiClient.post(`/companies/${companyId}/inbox/${it.id}/reanalyze`)).data;
      const ex = r.extracted || {};
      toast.success("Beleg gründlich neu geprüft", { description: [ex.supplier_name, ex.total_amount != null ? `CHF ${Number(ex.total_amount).toFixed(2)}` : null, ex.suggested_account, r.kind].filter(Boolean).join(" · ") });
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Neuanalyse fehlgeschlagen"); }
    finally { setReanalyzingId(null); }
  };


  const restoreItem = async (it) => {
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${it.id}/restore`);
      toast.success("Dokument wiederhergestellt — zurück im Posteingang");
      refresh();
    } catch (e) {
      toast.error("Wiederherstellen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <ReverseChargeThresholdCard companyId={companyId} />

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <InboxIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Posteingang</h1>
            <p className="text-sm text-slate-500">
              Belege & Lieferantenrechnungen — KI klassifiziert automatisch
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter258: Direkt-Links zu Anbieter-Rechnungsportalen */}
          <Button variant="outline" size="sm" onClick={() => setShowSources(true)} data-testid="inbox-sources-button" title="Direktlinks zu Anbieter-Portalen (z.B. Swisscom), wo du Rechnungen selbst herunterladen kannst">
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Beleg-Portale
          </Button>
          {/* Iter152: Per-Firma Gmail-Verknüpfung deaktiviert zugunsten der zentralen SA-Inbox.
              Endpoint bleibt für Sonderfälle bestehen. */}
          <Button variant="outline" size="sm" onClick={loadMailConfig} data-testid="inbox-mail-button" title="Zeigt die zentrale E-Mail-Adresse, an die du Belege und Quittungen weiterleiten kannst">
            <Mail className="h-4 w-4 mr-1.5" />
            Per E-Mail empfangen
          </Button>
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="inbox-refresh">
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Abo-Serien: gleicher Lieferant + Betrag, eigene Nummern/Daten → alle auf einmal buchen */}
      <SubscriptionSeriesCard companyId={companyId} refreshKey={items.map((x) => x.id).join(",")} onBooked={refresh} />

      {/* Pflicht-Prüfung: zusammengehörige Dokumente bestätigen oder trennen */}
      <RelationReviewDialog
        companyId={companyId}
        refreshKey={items.map((x) => x.id).join(",")}
        onResolved={refresh}
        onPreview={(id) => { const it = items.find((x) => x.id === id); if (it) openPreview(it); }}
      />

      {/* Kartenzahlungen ohne Beleg (aus dem Bankauszug) */}
      <MissingReceiptsPanel
        companyId={companyId}
        refreshKey={items.length}
        onUploadFor={(it) => {
          setUploadOpts((o) => ({ ...o, note: `Kartenzahlung ${it.purchase_date?.split("-").reverse().join(".")} CHF ${Number(it.amount).toFixed(2)} ${it.merchant}${it.last4 ? ` (Karte …${it.last4})` : ""}` }));
          toast.info("Notiz vorbereitet – jetzt den Beleg auswählen");
          fileInputRef.current?.click();
        }}
      />

      {/* Iter313: Zweck / Notiz / privat bezahlt beim Upload */}
      <InboxUploadOptions companyId={companyId} value={uploadOpts} onChange={setUploadOpts} />

      {/* Upload Drop-Zone */}
      <Card
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={onDrop}
        className="p-8 border-2 border-dashed border-slate-300 bg-slate-50/30 hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        data-testid="inbox-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="inbox-file-input"
        />
        <div className="flex flex-col items-center text-center gap-2">
          <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <Upload className={`h-6 w-6 text-indigo-600 ${uploading ? "animate-pulse" : ""}`} />
          </div>
          <p className="text-base font-medium text-slate-900">
            {uploading ? `${activeUploads.length} Datei(en) werden hochgeladen — Fortschritt oben rechts` : "Belege & Rechnungen hier ablegen oder klicken"}
          </p>
          <p className="text-xs text-slate-500">
            PDF, JPG, PNG · max. 25 MB pro Datei · mehrere gleichzeitig möglich
          </p>
          <p className="text-xs text-indigo-600 flex items-center gap-1 mt-1">
            <Sparkles className="h-3 w-3" />
            KI erkennt Lieferant, Datum, Betrag, MWST automatisch
          </p>
        </div>
      </Card>

      <UploadIssuesBanner companyId={companyId} />

      {/* Stats + Filter Pills — auch bei leerem Posteingang sichtbar (Papierkorb erreichbar) */}
      {(stats.total > 0 || kindFilter === "trash") && <UsageBanner companyId={companyId} compact />}
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="inbox-stats">
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { id: "all", label: `Alle (${stats.total})`, tone: "slate" },
              { id: "bill", label: `Eingangsrechnungen (${stats.bills})`, tone: "indigo" },
              { id: "receipt", label: `Belege (${stats.receipts})`, tone: "emerald" },
              { id: "queue", label: `⚡ Freigabe-Queue (${queueItems.length})`, tone: "amber" },
              { id: "trash", label: `🗑 Papierkorb${trashCount ? ` (${trashCount})` : ""}`, tone: "rose" },
            ].map((p) => {
              const active = kindFilter === p.id;
              const activeTone = p.tone === "indigo"
                ? "bg-indigo-600 text-white"
                : p.tone === "emerald"
                ? "bg-emerald-600 text-white"
                : p.tone === "rose"
                ? "bg-rose-600 text-white"
                : p.tone === "amber"
                ? "bg-amber-600 text-white"
                : "bg-slate-900 text-white";
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setKindFilter(p.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    active ? activeTone : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`inbox-filter-${p.id}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {kindFilter !== "all" && (
            <button
              type="button"
              onClick={() => { setKindFilter("all"); resetInboxView(); }}
              className="text-[11px] text-slate-500 hover:text-slate-700 underline"
              data-testid="inbox-reset-view"
              title="Filter zurücksetzen"
            >
              ↻ Standard
            </button>
          )}
          {reviewItems.length > 0 && (
            <Button size="sm" onClick={() => openReview()} className="ml-auto bg-indigo-600 hover:bg-indigo-700" data-testid="inbox-review-start" title="Alle offenen Belege nacheinander ansehen, Notiz erfassen – KI prüft sofort (Pfeiltasten zum Blättern)">
              <Layers className="h-3.5 w-3.5 mr-1.5" />Durchblättern ({reviewItems.length})
            </Button>
          )}
          {kindFilter !== "trash" && <BulkGreenConfirmButton companyId={companyId} items={visibleItems} onDone={refresh} />}
          {kindFilter === "trash" && items.length > 1 && (
            <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="inbox-trash-restore-all"
              onClick={async () => {
                if (!window.confirm(`Alle ${items.length} verworfenen Dokumente zurück in den Posteingang holen?`)) return;
                try { const r = await apiClient.post(`/companies/${companyId}/inbox/trash/restore-all`); toast.success(`${r.data?.restored || 0} Dokumente wiederhergestellt`); refresh(); }
                catch (e) { toast.error(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen"); }
              }}>
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Alle {items.length} wiederherstellen
            </Button>
          )}
          {reviewItems.length > 0 && (
            <span className="hidden xl:inline text-xs text-slate-500 self-center" data-testid="inbox-keyboard-hint" title="Tastatur: Pfeil runter wählt den ersten Beleg, Enter bestätigt ihn (Auswahl springt weiter), B = bearbeiten, N = Notiz, Esc = Auswahl aufheben">
              <kbd className="rounded border border-slate-300 bg-slate-50 px-1 font-mono text-[10px]">↓</kbd> wählen · <kbd className="rounded border border-slate-300 bg-slate-50 px-1 font-mono text-[10px]">↵</kbd> bestätigen
            </span>
          )}
        </div>

      {/* Schritt 3: Konfidenz-Routing – Schalter, Schatten-Statistik, Bulk-Freigabe */}
      {kindFilter === "queue" && <InboxRoutingPanel companyId={companyId} mode="inbox" onBulkDone={refresh} />}

      {/* Iter291: Auszahlungsberichte (SumUp/Payrexx) mit Status */}
      <PayoutReportsCard companyId={companyId} reloadToken={payoutReload} />
      <CardSuggestionsBanner companyId={companyId} onApplied={refresh} />

      {/* Inbox List */}
      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500">Lädt …</Card>
      ) : visibleItems.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <InboxIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            {kindFilter === "trash"
              ? "Papierkorb leer — keine verworfenen Dokumente"
              : items.length === 0
              ? "Posteingang leer — alle Dokumente verarbeitet ✓"
              : kindFilter === "queue"
              ? "Freigabe-Queue leer — nichts zu prüfen ✓"
              : `Keine ${kindFilter === "bill" ? "Eingangsrechnungen" : "Belege"} im Filter`}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="inbox-list">
          {visibleItems.map((it) => {
            const meta = KIND_LABEL[it.kind] || KIND_LABEL.receipt;
            const Icon = meta.icon;
            const ex = it.extracted || {};
            const lowConfidence = (it.confidence || 0) < 0.6;
            const draggableCard = it.status !== "discarded";
            const isDropTarget = dragId && dragId !== it.id && dragOverId === it.id;
            return (
              <Card
                key={it.id}
                className={`p-4 transition-colors ${isDropTarget ? "border-violet-400 ring-2 ring-violet-300 bg-violet-50/40" : "hover:border-slate-300"} ${dragId === it.id ? "opacity-50" : ""} ${draggableCard ? "cursor-grab active:cursor-grabbing" : ""} ${focusId === it.id ? "ring-2 ring-emerald-400 border-emerald-400 bg-emerald-50/50" : ""}`}
                data-testid={`inbox-item-${it.id}`}
                data-focused={focusId === it.id ? "true" : undefined}
                onClick={() => setFocusId(it.id)}
                draggable={draggableCard}
                onDragStart={(e) => {
                  if (!draggableCard) return;
                  setDragId(it.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", it.id);
                }}
                onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                onDragOver={(e) => {
                  if (dragId && dragId !== it.id && draggableCard) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDragOverId(it.id);
                  }
                }}
                onDragLeave={() => { if (dragOverId === it.id) setDragOverId(null); }}
                onDrop={(e) => {
                  if (dragId && dragId !== it.id && draggableCard) {
                    e.preventDefault();
                    e.stopPropagation();
                    const src = dragId;
                    setDragId(null);
                    setDragOverId(null);
                    bundleItems(src, it.id);
                  }
                }}
              >
                {it.status === "discarded" && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-900" data-testid={`inbox-discarded-info-${it.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Verworfen{it.discarded_at ? ` am ${formatDateTime(it.discarded_at)}` : ""}{it.discarded_by ? ` von ${it.discarded_by}` : ""} · kein Aufwand gebucht. Mit «Wiederherstellen» kommt der Beleg zurück in den Posteingang.</span>
                  </div>
                )}
                <UnknownCardBanner companyId={companyId} item={it} onRefresh={refresh} onNote={openNoteEdit} privateCompanies={privateCompanies} isPrivate={company?.company_type === "privat"} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                {it.status === "pending" && it.booked_page_of && <div className="mb-2"><BookedPageHint companyId={companyId} item={it} onDone={refresh} compact /></div>}
                {it.status === "pending" && businessCompanies.length > 0 && (
                  <div className="mb-2 rounded-md border border-teal-200 bg-teal-50/60 px-2.5 py-1.5" data-testid={`inbox-reclaim-row-${it.id}`}>
                    <ReclaimBusinessAction companyId={companyId} item={it} companies={businessCompanies} onDone={refresh} compact />
                  </div>
                )}
                <div className="flex items-start gap-4 flex-wrap">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color} border`} title={draggableCard ? "Ziehen & auf einen anderen Eintrag fallen lassen, um beide zu bündeln" : undefined}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0 2xl:basis-[36rem]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${meta.color} border`}>{meta.text}</Badge>
                      <span className="text-xs text-slate-500">
                        {Math.round((it.confidence || 0) * 100)}% sicher
                      </span>
                      {lowConfidence && (
                        <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Bitte prüfen
                        </span>
                      )}
                      {(it.source === "email" || it.source === "email_gmail" || it.source === "email_central") && (
                        <span className="text-[10px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded" title={it.source_meta?.body_only ? "Kein Anhang – die Quittung stand im E-Mail-Text und wurde als PDF übernommen" : it.source_meta?.subject || ""} data-testid={`inbox-source-email-${it.id}`}>
                          <Mail className="h-3 w-3 inline mr-0.5" /> per Mail{it.source_meta?.body_only ? " · aus Mailtext" : ""}
                        </span>
                      )}
                      {it.source === "whatsapp" && (
                        <span className="text-[10px] text-green-700 bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
                          📱 WhatsApp
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mt-1 truncate">
                      {it.kind === "bonus_statement" ? `Bonusabrechnung${ex.employee_name ? " · " + ex.employee_name : ""}` : (ex.supplier_name || it.file_name || "Unbenannt")}
                    </h3>
                    {focusId === it.id && it.status === "pending" && (
                      <p className="text-[11px] text-emerald-800 mt-1 inline-flex items-center gap-1.5 rounded-md bg-emerald-100/70 border border-emerald-200 px-2 py-0.5" data-testid={`inbox-focus-hint-${it.id}`}>
                        <kbd className="rounded border border-emerald-300 bg-white px-1 font-mono text-[10px]">↵</kbd> bestätigen
                        <span className="text-emerald-400">·</span>
                        <kbd className="rounded border border-emerald-300 bg-white px-1 font-mono text-[10px]">↑↓</kbd> wechseln
                        <span className="text-emerald-400">·</span>
                        <kbd className="rounded border border-emerald-300 bg-white px-1 font-mono text-[10px]">B</kbd> bearbeiten
                        <span className="text-emerald-400">·</span>
                        <kbd className="rounded border border-emerald-300 bg-white px-1 font-mono text-[10px]">N</kbd> Notiz
                        <span className="text-emerald-400">·</span>
                        <kbd className="rounded border border-emerald-300 bg-white px-1 font-mono text-[10px]">Esc</kbd>
                      </p>
                    )}
                    {(it.reopened_at || it.source_meta?.from_receipt_id) && (
                      <p className="text-[11px] text-amber-800 mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5" data-testid={`inbox-returned-${it.id}`}>
                        <Undo2 className="h-3 w-3" /> {(it.reasons || [])[0] || "Aus der Belegliste zurück in den Posteingang – bitte Datum, Art und Konto prüfen"}
                      </p>
                    )}
                    {it.source_meta?.expense_claim && (
                      <p className="text-xs text-teal-800 mt-1 inline-flex items-center gap-1 rounded-md bg-teal-50 border border-teal-200 px-2 py-0.5" data-testid={`inbox-expense-claim-${it.id}`}>
                        Spesen – Rückerstattung an {it.source_meta.expense_claim.employee_name}
                        {it.source_meta.purpose_label ? ` · ${it.source_meta.purpose_label}` : ""}
                        {" · Buchung Aufwand an 2060 · wird mit dem nächsten Lohnlauf ausbezahlt"}
                      </p>
                    )}
                    {!it.source_meta?.expense_claim && it.source_meta?.purpose_label && (
                      <p className="text-[11px] text-slate-500 mt-1" data-testid={`inbox-purpose-${it.id}`}>Zweck: {it.source_meta.purpose_label}</p>
                    )}
                    {it.kind === "bonus_statement" && (
                      <p className="text-xs text-fuchsia-800 mt-1" data-testid={`inbox-bonus-hint-${it.id}`}>
                        Lohnunterlage – keine Kreditorenrechnung. Der Bonus wird im Lohnlauf abgerechnet
                        {ex.employee_name ? ` (Mitarbeiter: ${ex.employee_name})` : " (Mitarbeiter nicht erkannt)"}.
                      </p>
                    )}
                    {it.kind === "insurance_benefit" && (
                      <p className="text-xs text-teal-800 mt-1 inline-flex items-center gap-1 rounded-md bg-teal-50 border border-teal-200 px-2 py-0.5" data-testid={`inbox-benefit-hint-${it.id}`}>
                        Zahlung an uns{ex.employee_name ? ` · ${ex.employee_name}` : ""}{ex.benefit_period ? ` · ${ex.benefit_period}` : ""}
                        {` · Buchung Soll ${ex.receivable_account || "1100"} / Haben ${String(ex.suggested_account || "5740").slice(0, 4)} (Aufwandsminderung)`}
                      </p>
                    )}
                    {ex.payment_plan?.installments?.length > 0 && (
                      <p className="text-xs text-indigo-800 mt-1 inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-200 px-2 py-0.5" data-testid={`inbox-payment-plan-${it.id}`}>
                        {ex.payment_plan.kind === "vat" ? "MWST-Zahlungsplan" : "Zahlungsplan"} · {ex.payment_plan.installments.length} Raten · total {fmtChf(ex.payment_plan.total)} · 1. Rate {fmtDate(ex.payment_plan.installments[0].due_date)} {fmtChf(ex.payment_plan.installments[0].amount)}
                        {" · bleibt offen bis alle Raten bezahlt sind"}{ex.payment_plan.kind === "vat" ? " · Buchung MWST-Konto an Kreditor ESTV" : ""}
                      </p>
                    )}
                    {!ex.is_credit_note && ex.credit_note_rejected && (
                      <p className="text-xs text-amber-800 mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5" data-testid={`inbox-credit-rejected-${it.id}`} title={ex.credit_note_rejected}>
                        <Undo2 className="h-3 w-3" /> Gutschrift-Wortlaut erkannt, aber kein passender Kaufbeleg – als normaler Beleg behandelt
                      </p>
                    )}
                    {ex.is_credit_note && ex.credit_note_hint && !ex.credit_of_receipt_id && (
                      <p className="text-xs text-amber-800 mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5" data-testid={`inbox-credit-nohint-${it.id}`}>
                        <Undo2 className="h-3 w-3" /> {ex.credit_note_hint}
                      </p>
                    )}
                    {ex.is_credit_note && (
                      <p className="text-xs text-teal-800 mt-1 inline-flex items-center gap-1 rounded-md bg-teal-50 border border-teal-200 px-2 py-0.5" data-testid={`inbox-credit-hint-${it.id}`}>
                        <Undo2 className="h-3 w-3" /> Gutschrift / Rückgabe · Geld kommt zurück – die Bank-Gutschrift wird im Bankabgleich zugeordnet und der Aufwand auf {String(ex.suggested_account || "dem Aufwandskonto").slice(0, 4)} storniert
                      </p>
                    )}
                    {ex.is_credit_note && (
                      <div className="mt-1 flex items-center gap-2 flex-wrap text-xs" data-testid={`inbox-credit-origin-${it.id}`} onClick={(e) => e.stopPropagation()}>
                        <span className="text-slate-500">Ursprünglicher Kauf:</span>
                        {(it.credit_origin_candidates || []).length > 0 || ex.credit_of_receipt_id ? (
                          <select
                            className="h-7 rounded-md border border-slate-300 bg-white px-1.5 text-xs max-w-md"
                            value={ex.credit_of_receipt_id || ""}
                            onChange={(e) => setCreditOrigin(it, e.target.value)}
                            data-testid={`inbox-credit-origin-select-${it.id}`}
                          >
                            <option value="">– nicht verknüpfen –</option>
                            {ex.credit_of_receipt_id && !(it.credit_origin_candidates || []).some((c) => c.id === ex.credit_of_receipt_id) && (
                              <option value={ex.credit_of_receipt_id}>{ex.credit_of_receipt_label || ex.credit_of_receipt_id}</option>
                            )}
                            {(it.credit_origin_candidates || []).map((c) => (
                              <option key={c.id} value={c.id}>{c.items_matched?.length ? `✓ Artikel ${c.items_matched.join(", ")} · ` : ""}{c.description} · {fmtDate(c.date)} · {fmtChf(c.amount)}{c.exact_amount ? " · gleicher Betrag" : c.remaining < c.amount ? ` · offen ${fmtChf(c.remaining)}` : ""}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-slate-400 italic">kein Kaufbeleg dieses Händlers in den letzten 180 Tagen gefunden</span>
                        )}
                        {ex.credit_of_receipt_id && (
                          <button type="button" onClick={() => navigate(`/receipts?highlight=${ex.credit_of_receipt_id}`)} className="text-indigo-700 hover:text-indigo-900 underline underline-offset-2" data-testid={`inbox-credit-origin-open-${it.id}`}>
                            Kaufbeleg anzeigen
                          </button>
                        )}
                        {ex.credit_origin_auto?.items?.length > 0 && ex.credit_of_receipt_id && (
                          <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-800" data-testid={`inbox-credit-origin-auto-${it.id}`}>automatisch verknüpft – {ex.credit_origin_auto.reason}</span>
                        )}
                        {it.credit_item_terms?.length > 0 && !ex.credit_of_receipt_id && (
                          <span className="text-[11px] text-slate-500" data-testid={`inbox-credit-items-${it.id}`}>Rückgabe: {it.credit_item_terms.join(", ")}</span>
                        )}
                      </div>
                    )}
                    {ex.is_credit_note && it.bank_refund_match && (
                      <div className="mt-1 flex items-start gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-900" data-testid={`inbox-bank-refund-${it.id}`}>
                        <Landmark className="h-3.5 w-3.5 shrink-0 mt-px" />
                        <span><strong>Bank-Gutschrift gefunden</strong> · +{fmtChf(it.bank_refund_match.amount)} am {fmtDate(it.bank_refund_match.date)} · «{it.bank_refund_match.description.slice(0, 70)}». Wird beim Bestätigen automatisch mit dieser Rückgabe verknüpft – kein Ertrag, sondern Aufwand-Korrektur.</span>
                      </div>
                    )}
                    {ex.is_credit_note && !ex.credit_of_receipt_id && it.credit_origin_pending && (
                      <div className="mt-1 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid={`inbox-credit-origin-pending-${it.id}`}>
                        <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                        <span><strong>Ursprünglicher Kauf liegt noch im Posteingang</strong> · «{it.credit_origin_pending.file_name}»{it.credit_origin_pending.date ? ` · ${fmtDate(it.credit_origin_pending.date)}` : ""}{it.credit_origin_pending.amount != null ? ` · ${fmtChf(it.credit_origin_pending.amount)}` : ""} (Artikel {it.credit_origin_pending.items_matched.join(", ")} gefunden). Zuerst den Kauf bestätigen – die Gutschrift wird dann automatisch damit verknüpft.</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-600 mt-1 flex-wrap">
                      {it.status === "pending" ? (
                        <InlineDateEdit companyId={companyId} item={it} field="invoice_date" label="Datum" onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                      ) : (
                        <span>Datum: <b>{fmtDate(ex.invoice_date)}</b></span>
                      )}
                      {ex.due_date && <span>Fällig: <b>{fmtDate(ex.due_date)}</b></span>}
                      {ex.paid_date && (it.status === "pending" ? (
                        <span className="text-emerald-700" data-testid={`inbox-paid-date-${it.id}`}><InlineDateEdit companyId={companyId} item={it} field="paid_date" label="Bezahlt/belastet am" onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} /></span>
                      ) : (
                        <span className="text-emerald-700" data-testid={`inbox-paid-date-${it.id}`}>Bezahlt/belastet am: <b>{fmtDate(ex.paid_date)}</b></span>
                      ))}
                      {it.status === "pending" ? (
                        <InlineAmountEdit companyId={companyId} item={it} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                      ) : (
                        <span>Betrag: <b>{fmtChf(ex.total_amount, ex.currency || "CHF")}</b>{ex.tip?.auto && (
                          <span className="ml-1 inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-900" title={ex.tip.reason} data-testid={`inbox-tip-auto-${it.id}`}>
                            Beleg {fmtChf(ex.tip.receipt_amount)} + Trinkgeld {fmtChf(ex.tip.amount)} → {ex.tip.account}
                            <button type="button" className="underline underline-offset-2 hover:text-amber-950" data-testid={`inbox-tip-decline-${it.id}`}
                              onClick={async (e) => { e.stopPropagation(); try { await apiClient.post(`/companies/${companyId}/inbox/${it.id}/tip/decline`); toast.success("Kein Trinkgeld – Betrag auf Belegtotal zurückgesetzt"); refresh(); } catch (err) { toast.error(err.response?.data?.detail || "Fehlgeschlagen"); } }}>kein Trinkgeld</button>
                          </span>
                        )}{ex.amount_fix && <span className="ml-1 text-emerald-700" title={ex.amount_fix.reason} data-testid={`inbox-amount-fix-${it.id}`}>✓ Netto {fmtChf(ex.amount_fix.from)} → Brutto</span>}</span>
                      )}
                      {ex.payment_card && (
                        <span className={`inline-flex items-center gap-1 ${ex.payment_card.scope === "other_company" ? "text-rose-700" : ex.payment_card.scope === "employee_private" ? "text-teal-800" : "text-slate-700"}`} data-testid={`inbox-payment-card-${it.id}`} title="Erkanntes Zahlungsmittel">
                          <CreditCard className="h-3 w-3" />
                          {ex.payment_card.method === "cash" ? "Bar" : ex.payment_card.method === "twint" ? "TWINT" : `${ex.payment_card.brand || "Karte"}${ex.payment_card.last4 ? ` …${ex.payment_card.last4}` : ""}`}
                          {ex.payment_card.scope === "company" && ` · Firmenkarte${ex.payment_card.holder_name ? ` (${ex.payment_card.holder_name})` : ""}${ex.payment_card.alias_of_last4 ? ` · ${ex.payment_card.wallet === "google_pay" ? "Google Pay" : ex.payment_card.wallet === "samsung_pay" ? "Samsung Pay" : "Apple Pay"} von …${ex.payment_card.alias_of_last4}` : ""}`}
                          {ex.payment_card.scope === "employee_private" && ` · Privatkarte ${ex.payment_card.holder_name || ""} → Spesen`}
                          {ex.payment_card.scope === "other_company" && ` · Karte von ${ex.payment_card.other_company_name}`}
                        </span>
                      )}
                      {ex.invoice_number && (
                        <span data-testid={`inbox-invoice-number-${it.id}`}>Rg-Nr: <b className="font-mono">{ex.invoice_number}</b></span>
                      )}
                      {ex.iban && <span className="font-mono text-[10px] text-slate-500">{ex.iban}</span>}
                      {ex.currency && ex.currency !== "CHF" && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                          🌍 Fremdwährung — wird beim Buchen umgerechnet
                        </span>
                      )}
                    </div>
                    {/* Iter87: KI-Konto-Vorschlag prominent anzeigen */}
                    {ex.suggested_account && (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] bg-violet-50 text-violet-700 border border-violet-200 rounded-md px-2 py-1">
                        <Sparkles className="h-3 w-3" />
                        <span>{ex.rule_match ? "📌 Feste Regel:" : ex.account_source === "recheck" ? "✔ Neu geprüft:" : ex.account_source?.startsWith("keyword") || ex.account_source === "purpose" ? "📌 Kontierungsregel:" : "KI-Konto-Vorschlag:"}</span>
                        <b className="font-mono">{ex.suggested_account}</b>
                        <AccountConfidenceBadge extracted={ex} itemId={it.id} />
                        {ex.rule_match?.auto_book && (
                          <span className="rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[9px] font-semibold" title="Wird vollautomatisch verbucht (in den Buchungsregeln abschaltbar)">
                            AUTO
                          </span>
                        )}
                        {it.status === "pending" ? (
                          <InlineAccountSelect companyId={companyId} item={it} accounts={accounts} ensureAccounts={ensureAccounts} onOpenDialog={() => openAccountEdit(it)} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                        ) : (
                          <button onClick={() => openAccountEdit(it)} className="ml-0.5 text-violet-500 hover:text-violet-900 transition-colors" data-testid={`inbox-edit-account-${it.id}`} title="Konto anpassen">
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                    {!ex.suggested_account && it.status === "pending" && it.kind !== "bonus_statement" && it.kind !== "payout_report" && (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] bg-slate-50 text-slate-600 border border-slate-200 rounded-md px-2 py-1" data-testid={`inbox-no-account-${it.id}`}>
                        <span>Kein Konto-Vorschlag –</span>
                        <InlineAccountSelect companyId={companyId} item={it} accounts={accounts} ensureAccounts={ensureAccounts} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                        <span>Konto wählen</span>
                      </div>
                    )}
                    {it.kind !== "bonus_statement" && <InboxExtractionDetails item={it} />}
                    {it.status === "pending" && ex.account_split && (
                      <InboxSplitPanel companyId={companyId} item={it} accounts={accounts} ensureAccounts={ensureAccounts} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                    )}
                    {it.status === "pending" && it.routing && <div className="mt-1"><RoutingBadge item={it} /></div>}
                    <div className="mt-1 text-[10px] text-slate-400 truncate" title={it.file_name}>
                      📎 {it.file_name}
                    </div>
                    {/* Iter283: gebündelte Beilagen aus derselben E-Mail */}
                    {(it.attachments?.length > 0) && (
                      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/70" data-testid={`inbox-attachments-${it.id}`}>
                        <button
                          onClick={() => toggleAtts(it.id)}
                          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
                          data-testid={`inbox-attachments-toggle-${it.id}`}
                          title="Weitere Dokumente aus derselben E-Mail — nur das Hauptdokument wird verbucht"
                        >
                          {expandedAtts[it.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          <Paperclip className="h-3 w-3" />
                          {it.auto_merged
                            ? `${it.attachments.length} zusammengeführte Beilage${it.attachments.length === 1 ? "" : "n"} (gleiche Rechnungsnummer / zusammengehörig)${it.auto_merged.reviewed ? "" : " – bitte prüfen"}`
                            : it.attachments.length === 1
                            ? "1 weitere Beilage aus derselben E-Mail"
                            : `${it.attachments.length} weitere Beilagen aus derselben E-Mail`}
                        </button>
                        {expandedAtts[it.id] && (
                          <div className="border-t border-slate-200 divide-y divide-slate-100">
                            {it.attachments.map((a) => (
                              <div
                                key={a.file_id}
                                className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-slate-600"
                                data-testid={`inbox-attachment-${a.file_id}`}
                              >
                                <span className="flex-1 truncate" title={a.file_name}>
                                  <span className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600" data-testid={`inbox-attachment-page-${a.file_id}`}>Seite {(it.attachments || []).indexOf(a) + 2}</span>
                                  📄 {a.file_name}
                                  {attachmentKindHint(a.file_name) && (
                                    <span className="ml-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700" data-testid={`inbox-attachment-kind-${a.file_id}`}>
                                      {attachmentKindHint(a.file_name)}
                                    </span>
                                  )}
                                  {a.supplier_name && <span className="ml-1 text-slate-400">· {a.supplier_name}</span>}
                                  {a.total_amount != null && <span className="ml-1 text-slate-400">· {fmtChf(a.total_amount)}</span>}
                                </span>
                                <button
                                  onClick={() => previewAttachment(it, a)}
                                  className="text-slate-500 hover:text-slate-900 transition-colors"
                                  title="Beilage anzeigen"
                                  data-testid={`inbox-attachment-preview-${a.file_id}`}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {it.status !== "discarded" && (
                                  <>
                                    <button onClick={() => moveAttachment(it, a, -1)} disabled={(it.attachments || []).indexOf(a) === 0}
                                      className="text-slate-400 hover:text-slate-800 disabled:opacity-25 transition-colors" title="Seite nach vorne"
                                      data-testid={`inbox-attachment-up-${a.file_id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => moveAttachment(it, a, 1)} disabled={(it.attachments || []).indexOf(a) === (it.attachments || []).length - 1}
                                      className="text-slate-400 hover:text-slate-800 disabled:opacity-25 transition-colors" title="Seite nach hinten"
                                      data-testid={`inbox-attachment-down-${a.file_id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                                    <button
                                      onClick={() => promoteAttachment(it, a)}
                                      className="text-violet-500 hover:text-violet-800 transition-colors"
                                      title="Als Hauptdokument verwenden — dieses Dokument wird dann verbucht"
                                      data-testid={`inbox-attachment-promote-${a.file_id}`}
                                    >
                                      <Star className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => detachAttachment(it, a)}
                                      className="text-slate-500 hover:text-slate-800 transition-colors"
                                      title="Entfernen — Beilage wird nicht gelöscht, sondern wieder als eigenes Dokument in den Posteingang gestellt"
                                      data-testid={`inbox-attachment-detach-${a.file_id}`}
                                    >
                                      <Undo2 className="h-3.5 w-3.5" />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Iter326: Mahnung → Originalrechnung */}
                    <InboxReminderLink companyId={companyId} item={it} onDone={refresh} />
                    {/* Iter261: Duplikat-Warnung */}
                    <InboxChecksPanel companyId={companyId} item={it} onRefresh={refresh} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                    {it.duplicate_warning && (
                      <div
                        className={`mt-2 flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] ${it.duplicate_warning.already_booked ? "border-rose-300 bg-rose-50 text-rose-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}
                        data-testid={`inbox-duplicate-warning-${it.id}`}
                      >
                        <span>{it.duplicate_warning.already_booked ? "🛑" : "⚠️"}</span>
                        <span>
                          {it.duplicate_warning.already_booked ? (
                            <>
                              <strong>{it.duplicate_warning.certain ? "Bereits verbucht!" : "Möglicherweise bereits verbucht"}</strong> — {it.duplicate_warning.reason} wie{" "}
                              {it.duplicate_warning.others.map((o) => `«${o.file_name}»${o.confirmed_at ? ` (bestätigt am ${formatDateTime(o.confirmed_at)})` : ""}`).join(", ")}.
                              {" "}<strong>Nicht nochmals bestätigen</strong> — mit 🗑️ verwerfen, sonst wird der Aufwand doppelt gebucht.
                              <div className="mt-1.5 flex flex-wrap gap-2" data-testid={`inbox-duplicate-booked-${it.id}`}>
                                {it.duplicate_warning.others.map((o) => (
                                  <div key={o.id} className="flex items-center gap-2 rounded border border-rose-200 bg-white px-2 py-1" data-testid={`inbox-duplicate-booked-${it.id}-${o.id}`}>
                                    {o.has_file ? <DocThumb url={`/companies/${companyId}/inbox/${o.id}/file`} title={o.file_name} onOpen={() => setBookedPreview({ ...o, url: `/companies/${companyId}/inbox/${o.id}/file` })} className="h-10 w-10" testid={`inbox-duplicate-booked-thumb-${o.id}`} />
                                      : <span className="h-10 w-10 rounded border border-dashed border-rose-200" />}
                                    <div className="text-[11px] leading-tight text-rose-900">
                                      <div className="font-semibold">Gebuchter Beleg · {o.date ? formatDate(o.date) : "–"} · {o.amount != null ? `CHF ${Number(o.amount).toFixed(2)}` : "–"}</div>
                                      <div>{o.supplier || "–"}{o.invoice_number ? ` · Nr. ${o.invoice_number}` : ""}{o.confirmed_at ? ` · gebucht ${formatDate(o.confirmed_at)}` : ""}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <>
                              <strong>Mögliches Duplikat</strong> — {it.duplicate_warning.reason} wie{" "}
                              {it.duplicate_warning.others.map((o) => `«${o.file_name}» (${o.kind === "bill" ? "Eingangsrechnung" : "Beleg"})`).join(", ")}.
                              {" "}Nur <strong>eines</strong> bestätigen, sonst wird der Aufwand doppelt gebucht — das andere mit 🗑️ verwerfen.
                            </>
                          )}
                          <button
                            onClick={() => openCompare(it)}
                            className={`ml-1.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-medium transition-colors ${it.duplicate_warning.already_booked ? "border-rose-400 bg-white hover:bg-rose-100 text-rose-800" : "border-amber-400 bg-white hover:bg-amber-100 text-amber-800"}`}
                            data-testid={`inbox-duplicate-compare-${it.id}`}
                            title="Beide Dokumente nebeneinander anzeigen, um das Duplikat zu prüfen"
                          >
                            <ZoomIn className="h-3 w-3" />
                            Lupe: beide vergleichen
                          </button>
                          <button
                            onClick={() => discard(it, true)}
                            className={`ml-1.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-medium transition-colors ${it.duplicate_warning.already_booked ? "border-rose-400 bg-white hover:bg-rose-100 text-rose-800" : "border-amber-400 bg-white hover:bg-amber-100 text-amber-800"}`}
                            data-testid={`inbox-duplicate-discard-${it.id}`}
                            title="Verwirft dieses Dokument mit einem Klick — das andere bleibt erhalten"
                          >
                            <Trash2 className="h-3 w-3" />
                            {it.duplicate_warning.already_booked ? "Kopie verwerfen" : "Dieses verwerfen"}
                          </button>
                        </span>
                      </div>
                    )}
                    {/* Iter170: WhatsApp Notiz + Note-Hints anzeigen */}
                    <WarrantyHint item={it} onToggle={toggleWarranty} onAttachToggle={setAttachCert} />
                    <CompanionHint item={it} />
                    <InboxCompanySuggestion companyId={companyId} item={it} onDone={refresh} />
                    {it.source_meta?.moved_from_company_name && (
                      <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1" data-testid={`inbox-item-moved-from-${it.id}`}>
                        <span className="inline-flex items-center gap-1.5"><ArrowRightLeft className="h-3 w-3" /> Übertragen aus «{it.source_meta.moved_from_company_name}»</span>
                        <button
                          type="button"
                          disabled={undoingId === it.id}
                          onClick={() => undoMove(it)}
                          className="inline-flex items-center gap-1 rounded border border-sky-300 bg-white px-1.5 py-px font-medium text-sky-800 hover:bg-sky-100 disabled:opacity-50"
                          title="Übertragung rückgängig machen – Beleg geht zurück in den Posteingang der Ursprungsfirma"
                          data-testid={`inbox-item-undo-move-${it.id}`}
                        >
                          {undoingId === it.id ? <RotateCw className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Zurück zu {it.source_meta.moved_from_company_name}
                        </button>
                      </div>
                    )}
                    {it.source_meta?.recharge && (
                      <div className="mt-2 inline-flex flex-wrap items-center gap-1.5 text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1" data-testid={`inbox-item-recharge-${it.id}`}>
                        <ArrowRightLeft className="h-3 w-3" />
                        {it.source_meta.recharge.mode === "invoice"
                          ? <>Weiterbelastet an «{it.source_meta.recharge.target_company_name}» – Rechnung <strong>{it.source_meta.recharge.invoice_number}</strong> gebucht</>
                          : <>Bezahlt von «{it.source_meta.recharge.from_company_name}» – beim Bestätigen Aufwand an Kontokorrent {it.source_meta.recharge.counter_account}</>}
                      </div>
                    )}
                    {it.source_meta?.user_note && (
                      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-700 bg-green-50/60 border border-green-200 rounded px-2 py-1.5" data-testid={`inbox-item-note-${it.id}`}>
                        <span className="text-green-700">💬</span>
                        <span className="italic">„{it.source_meta.user_note}“</span>
                        {it.status !== "discarded" && (
                          <button
                            onClick={() => openNoteEdit(it)}
                            className="text-green-600 hover:text-green-900 transition-colors"
                            title="Notiz bearbeiten"
                            data-testid={`inbox-note-edit-inline-${it.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                        {it.status !== "discarded" && (
                          <button
                            onClick={() => recheckItem(it)}
                            disabled={recheckingId === it.id}
                            className="ml-auto inline-flex items-center gap-1 rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 hover:bg-violet-100 disabled:opacity-50 not-italic"
                            title="Kontierung anhand Notiz und gesamtem Belegtext neu prüfen lassen"
                            data-testid={`inbox-recheck-btn-${it.id}`}
                          >
                            {recheckingId === it.id ? <RotateCw className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Neu prüfen
                          </button>
                        )}
                      </div>
                    )}
                    <InboxRecheckCard companyId={companyId} item={it} onChanged={refresh} onMove={(item, targetId) => { setMoveItem(item); setMoveTarget(targetId); }} />
                    {it.source_meta?.note_hints && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {it.source_meta.note_hints.category && (
                          <span className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
                            📂 {it.source_meta.note_hints.category}
                          </span>
                        )}
                        {it.source_meta.note_hints.cost_center_name && (
                          <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            🏷️ {it.source_meta.note_hints.cost_center_name}
                          </span>
                        )}
                        {it.source_meta.note_hints.account_number && (
                          <span className="text-[10px] bg-slate-50 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5 font-mono">
                            💼 {it.source_meta.note_hints.account_number}
                          </span>
                        )}
                        {it.source_meta.note_hints.autobook && (
                          <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                            ⚡ #autobuchen
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap basis-full order-first justify-end 2xl:basis-auto 2xl:order-none 2xl:justify-start 2xl:ml-auto" data-testid={`inbox-item-actions-${it.id}`}>
                    <Button variant="outline" size="sm" onClick={() => openPreview(it)} data-testid={`inbox-preview-${it.id}`} title="Original-Dokument (PDF/Foto) in der Vorschau öffnen">
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      Anzeigen
                    </Button>
                    {it.status === "pending" && it.source_meta?.body_only && (
                      <Button variant="outline" size="sm" onClick={() => rerenderEmail(it)} disabled={rerenderingId === it.id} data-testid={`inbox-rerender-email-${it.id}`}
                        className="border-sky-300 text-sky-800 hover:bg-sky-50" title="E-Mail-Beleg ohne Anhang im Original-Layout (Tabellen, Logos) neu als PDF erzeugen">
                        {rerenderingId === it.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
                        Original-Layout neu erzeugen
                      </Button>
                    )}
                    {it.status === "pending" && /\.(csv|xlsx)$/i.test(it.file_name || "") && (
                      <Button variant="outline" size="sm" onClick={() => reprocessPayout(it)} disabled={reprocessingId === it.id} data-testid={`inbox-reprocess-payout-${it.id}`}
                        className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title="Export als Auszahlungsbericht (TWINT, SumUp, Payrexx) verarbeiten: Brutto, Gebühren und überwiesener Betrag werden ausgelesen und der Bankgutschrift zugeordnet">
                        {reprocessingId === it.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Banknote className="h-3.5 w-3.5 mr-1" />}
                        Als Auszahlung verarbeiten
                      </Button>
                    )}
                    {it.status === "pending" && it.file_name && (
                      <Button variant="outline" size="sm" onClick={() => openReview(it.id)} data-testid={`inbox-review-${it.id}`} title="Ab hier durchblättern – Notiz erfassen, KI prüft sofort">
                        <Layers className="h-3.5 w-3.5 mr-1" />
                        Blättern
                      </Button>
                    )}
                    {it.status === "pending" && it.file_name && (
                      <Button variant="outline" size="sm" disabled={reanalyzingId === it.id} onClick={() => reanalyze(it)} data-testid={`inbox-reanalyze-${it.id}`} title="Gründlich neu prüfen: OCR, KI, Dokumenttyp und Ampel laufen komplett neu (Notiz bleibt)">
                        {reanalyzingId === it.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />}
                        Neu prüfen
                      </Button>
                    )}
                    {it.status === "discarded" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreItem(it)}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        data-testid={`inbox-restore-${it.id}`}
                        title="Dokument zurück in den Posteingang holen"
                      >
                        <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                        Wiederherstellen
                      </Button>
                    ) : (
                    <>
                    {it.kind !== "loan_contract" && it.kind !== "bonus_statement" && !ex.is_credit_note && (
                      <Button variant="outline" size="sm" onClick={() => flipKind(it)} data-testid={`inbox-flip-${it.id}`} title={it.kind === "bill" ? "Kategorie wechseln: Dieses Dokument als Beleg/Quittung (bereits bezahlt) einstufen" : "Kategorie wechseln: Dieses Dokument als Eingangsrechnung (noch zu bezahlen) einstufen"}>
                        <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                        {it.kind === "receipt" ? "→ Eingangsrechnung" : "→ Beleg"}
                      </Button>
                    )}
                    {it.kind !== "document" && it.kind !== "bonus_statement" && (
                      <Button variant="outline" size="sm" onClick={() => fileAsDocument(it)} data-testid={`inbox-as-document-${it.id}`} title="Nicht buchen, sondern als Firmendokument im Dokumentenordner ablegen (Protokoll, Schreiben, Bestätigung …)">
                        <FolderOpen className="h-3.5 w-3.5 mr-1" />
                        Dokument
                      </Button>
                    )}
                    {(it.kind === "receipt" || it.kind === "bill") && !it.warranty?.detected && (
                      <Button variant="outline" size="sm" onClick={() => toggleWarranty(it, true)} data-testid={`inbox-mark-warranty-${it.id}`} title="Dieser Kauf hat eine Garantie – beim Bestätigen zusätzlich im Garantiebereich ablegen">
                        <Shield className="h-3.5 w-3.5 mr-1" />
                        Garantie
                      </Button>
                    )}
                    {(it.kind === "receipt" || it.kind === "bill") && (
                      <Button variant="outline" size="sm" onClick={() => toggleCreditNote(it)} className={ex.is_credit_note ? "border-teal-400 bg-teal-50 text-teal-900" : ""} data-testid={`inbox-credit-note-${it.id}`} title={ex.is_credit_note ? "Markierung aufheben – wieder als normaler Kaufbeleg behandeln" : "Dieses Dokument ist eine Gutschrift/Rückgabe (Geld kommt zurück) – nicht als Aufwand buchen"}>
                        <Undo2 className="h-3.5 w-3.5 mr-1" />
                        {ex.is_credit_note ? "Gutschrift ✓" : "Als Gutschrift"}
                      </Button>
                    )}
                    {it.kind === "bonus_statement" && (
                      <Button variant="outline" size="sm" onClick={() => window.open("/payroll", "_blank")} data-testid={`inbox-bonus-payroll-${it.id}`} title="Zur Lohnbuchhaltung (GF-Boni / Lohnläufe)">
                        <Trophy className="h-3.5 w-3.5 mr-1" />
                        Zum Lohnlauf
                      </Button>
                    )}
                    {it.kind !== "bonus_statement" && (
                    <Button variant="outline" size="sm" onClick={() => openNoteEdit(it)} data-testid={`inbox-note-${it.id}`} title="Notiz hinzufügen — beschreibe, wofür dieser Beleg ist. Die KI passt den Konto-Vorschlag daran an.">
                      <StickyNote className="h-3.5 w-3.5 mr-1" />
                      Notiz
                    </Button>
                    )}
                    {it.kind !== "bonus_statement" && myCompanies.length > 1 && (
                    <Button variant="outline" size="sm" onClick={() => { setMoveItem(it); setMoveTarget(""); }} data-testid={`inbox-move-${it.id}`} title="Beleg intern an eine andere eigene Firma übertragen (z.B. falsche Firma gewählt)">
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
                      Firma
                    </Button>
                    )}
                    {it.kind !== "bonus_statement" && (
                    <Button variant="outline" size="sm" onClick={() => setEditItem(it)} data-testid={`inbox-edit-${it.id}`} title="Erkannte Daten (Lieferant, Datum, Betrag, MWST) vor dem Bestätigen korrigieren">
                      Bearbeiten
                    </Button>
                    )}
                    {it.kind === "loan_contract" ? (
                      <Button size="sm" onClick={() => postAsLoan(it)} className="bg-amber-600 hover:bg-amber-700" data-testid={`inbox-post-as-loan-${it.id}`} title="Erfasst dieses Dokument nicht als Aufwand, sondern als Darlehen">
                        <Banknote className="h-3.5 w-3.5 mr-1" />
                        Als Darlehen erfassen
                      </Button>
                    ) : it.kind === "warranty_certificate" ? (
                      <Button size="sm" onClick={() => fileAsWarranty(it)} className="bg-violet-700 hover:bg-violet-800" data-testid={`inbox-file-warranty-${it.id}`} title="Garantieschein in den Garantiebereich ablegen – es wird nichts gebucht">
                        <Shield className="h-3.5 w-3.5 mr-1" />
                        In Garantien ablegen
                      </Button>
                    ) : it.kind === "receipt_companion" ? (
                      <Button size="sm" onClick={() => fileAsPurchase(it)} disabled={!it.purchase_receipt} className="bg-stone-700 hover:bg-stone-800" data-testid={`inbox-file-purchase-${it.id}`} title={it.purchase_receipt ? "Beilage als Seite zum gebuchten Kaufbeleg legen – es wird nichts gebucht" : "Kein gebuchter Kaufbeleg gefunden"}>
                        <Link2 className="h-3.5 w-3.5 mr-1" />
                        Zum Kaufbeleg legen
                      </Button>
                    ) : it.kind === "contract" ? (
                      <Button size="sm" onClick={() => fileAsContract(it)} className="bg-sky-700 hover:bg-sky-800" data-testid={`inbox-file-contract-${it.id}`} title="Vertrag in den Verträge-Ordner ablegen – es wird nichts gebucht">
                        <FileSignature className="h-3.5 w-3.5 mr-1" />
                        In Verträge ablegen
                      </Button>
                    ) : it.kind === "document" ? (
                      <Button size="sm" onClick={() => fileAsDocument(it)} className="bg-amber-600 hover:bg-amber-700" data-testid={`inbox-file-document-${it.id}`} title={`Firmendokument in den Dokumentenordner ablegen (Vorschlag: ${it.document?.folder || "Sonstiges"}) – es wird nichts gebucht`}>
                        <FolderOpen className="h-3.5 w-3.5 mr-1" />
                        In Dokumente ablegen
                      </Button>
                    ) : it.kind === "bonus_statement" ? (
                      <Button size="sm" onClick={() => postItem(it)} className="bg-fuchsia-700 hover:bg-fuchsia-800" data-testid={`inbox-file-bonus-${it.id}`} title="Als Lohnunterlage ablegen – es wird nichts gebucht">
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Als Lohnunterlage ablegen
                      </Button>
                    ) : it.kind === "insurance_benefit" ? (
                      <Button size="sm" onClick={() => postItem(it)} className="bg-teal-700 hover:bg-teal-800" data-testid={`inbox-post-benefit-${it.id}`} title="Bucht die Leistung als Forderung (Soll 1100 / Haben 5740) – wird beim Zahlungseingang auf der Bank automatisch ausgeglichen">
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Als Gutschrift buchen
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => postItem(it)} className={ex.is_credit_note ? "bg-teal-700 hover:bg-teal-800" : (it.checks?.requires_reverse_charge_decision && ex.reverse_charge == null) ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} data-testid={`inbox-post-${it.id}`} title={ex.is_credit_note ? "Erfasst die Gutschrift als Beleg (Geld zurück) – Bankgutschrift wird im Bankabgleich zugeordnet" : (it.checks?.requires_reverse_charge_decision && ex.reverse_charge == null) ? "Bezugsteuer-Entscheid nötig – öffnet den Beleg mit Ja/Nein, bevor gebucht wird" : "Übernimmt das Dokument in die Buchhaltung — als Beleg bzw. Eingangsrechnung"}>
                        <Check className="h-3.5 w-3.5 mr-1" />
                        {ex.is_credit_note ? "Gutschrift bestätigen" : (it.checks?.requires_reverse_charge_decision && ex.reverse_charge == null) ? "Bestätigen · Bezugsteuer?" : "Bestätigen"}
                      </Button>
                    )}
                    {it.source_meta?.expense_claim && (
                      <Button variant="outline" size="sm" onClick={() => rejectItem(it)} className="border-rose-300 text-rose-800" data-testid={`inbox-reject-${it.id}`} title="Spesen ablehnen (kein Geschäftsaufwand) – Mitarbeiter sieht Status und Grund im Portal">
                        Ablehnen
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => discard(it)} className="text-rose-700 hover:bg-rose-50" data-testid={`inbox-discard-${it.id}`} title="In den Papierkorb verschieben (wiederherstellbar)">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mail-Setup Dialog */}
      <ReceiptSourcesDialog open={showSources} onOpenChange={setShowSources} companyId={companyId} />
      <Dialog open={!!moveItem} onOpenChange={(v) => !v && setMoveItem(null)}>
        <DialogContent className="max-w-md" data-testid="inbox-move-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-slate-600" /> Beleg an andere Firma übertragen</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            «{moveItem?.extracted?.supplier_name || moveItem?.file_name}» wird in den Posteingang der Zielfirma verschoben und dort neu geprüft.
          </DialogDescription>
          <select className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} data-testid="inbox-move-target">
            <option value="">Zielfirma wählen …</option>
            {myCompanies.filter((c) => (c._id || c.id) !== companyId).map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
            ))}
          </select>
          <RechargeOptions companyId={companyId} item={moveItem} targetId={moveTarget} value={moveRecharge} onChange={setMoveRecharge} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveItem(null)}>Abbrechen</Button>
            <Button onClick={moveToCompany} disabled={!moveTarget || moving} data-testid="inbox-move-submit">
              {moving ? "Wird ausgeführt…" : moveRecharge === "invoice" ? "Rechnung erstellen" : moveRecharge === "kontokorrent" ? "Übertragen & Kontokorrent" : "Übertragen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMailDialog} onOpenChange={setShowMailDialog}>
        <DialogContent className="max-w-xl" data-testid="inbox-mail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Posteingang per E-Mail
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="central-inbox-hint">
              <div className="font-medium text-emerald-900 mb-1">✨ Zentrale Beleg-Adresse</div>
              <p className="text-emerald-800 leading-relaxed">
                Leite jeden Beleg einfach von deiner <strong>registrierten Business-E-Mail</strong>
                {mailConfig?.sender_email ? <> (<span className="font-mono">{mailConfig.sender_email}</span>)</> : " (z.B. der Adresse, mit der du dich hier angemeldet hast)"}
                {" "}an die zentrale Belegadresse weiter.
                Das System ordnet den Beleg automatisch deiner Firma zu — kein Setup pro Firma nötig.
              </p>
              {mailConfig?.central_inbox_email ? (
                <div className="mt-2">
                  <Label className="text-[11px] text-emerald-900">Quittungen & Belege senden an:</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={mailConfig.central_inbox_email}
                      readOnly
                      className="font-mono text-xs bg-white"
                      data-testid="central-inbox-email"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="central-inbox-email-copy"
                      onClick={() => { navigator.clipboard.writeText(mailConfig.central_inbox_email); toast.success("Adresse kopiert"); }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {!mailConfig.central_inbox_active && (
                    <p className="text-amber-700 mt-1">⚠️ Der zentrale Posteingang ist aktuell deaktiviert — bitte Administrator kontaktieren.</p>
                  )}
                </div>
              ) : (
                <p className="text-emerald-800 leading-relaxed mt-1">
                  💡 Die zentrale Adresse ist noch nicht hinterlegt — dein Administrator kann sie im
                  Super-Admin-Bereich (Zentraler E-Mail-Posteingang) konfigurieren.
                </p>
              )}
            </div>
            <p className="text-slate-700 text-xs">
              Alternative (Legacy): Falls dein Mail-Provider das nicht schafft, gibt es pro Firma eine
              eigene Webhook-URL für Inbound-Mail-Provider (Mailgun/Resend). Details siehe unten.
            </p>
            {mailConfig && (
              <>
                <div>
                  <Label className="text-xs">Mail-Adresse für diese Firma</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={mailConfig.mail_address} readOnly className="font-mono text-xs" data-testid="inbox-mail-address" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(mailConfig.mail_address); toast.success("Kopiert"); }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs space-y-2">
                  <p className="font-medium text-amber-900">⚙️ Setup-Schritte (einmalig durch Admin):</p>
                  <ol className="list-decimal list-inside text-amber-800 space-y-1">
                    <li>Mailgun- oder Resend-Konto erstellen (kostenloser Tier reicht)</li>
                    <li>Eigene Domain hinzufügen + verifizieren (DNS-Records)</li>
                    <li>Inbound-Route konfigurieren — Webhook-URL:
                      <div className="mt-1 font-mono text-[10px] bg-white border border-amber-300 rounded px-2 py-1 break-all">
                        {window.location.origin}{mailConfig.webhook_url}
                      </div>
                    </li>
                    <li>Mailgun-Mail-Adresse → JSON-Payload an die Webhook-URL senden</li>
                  </ol>
                  <p className="text-amber-700 italic mt-2">
                    Falls du das selbst nicht einrichten kannst: kontaktiere uns, wir richten es ein.
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowMailDialog(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      {editItem && (
        <EditInboxDialog
          item={editItem}
          onClose={() => setEditItem(null)}
          onPost={(override) => postItem(editItem, override)}
        />
      )}

      {/* Iter274: Notiz-Dialog — wofür ist dieser Beleg? */}
      <InboxNoteDialog item={noteEditItem} companyId={companyId} value={noteText} onChange={setNoteText} onClose={() => setNoteEditItem(null)} onSave={saveNote} onSaveRecheck={saveNoteAndRecheck} saving={noteSaving} />

      {/* Iter87: Preview Dialog (PDF/Bild in-app) */}
      {bookedPreview && <DocPreviewDialog open onClose={() => setBookedPreview(null)} url={bookedPreview.url} title={bookedPreview.file_name} testid="inbox-duplicate-booked-preview" />}
      {creditConfirm && (
        <CreditNoteConfirmDialog
          companyId={companyId}
          item={creditConfirm.item}
          onCancel={() => setCreditConfirm(null)}
          onConfirmCredit={() => { const c = creditConfirm; setCreditConfirm(null); postItem(c.item, { ...c.override, _creditConfirmed: true }); }}
          onConfirmNormal={async () => {
            const c = creditConfirm; setCreditConfirm(null);
            try {
              await apiClient.post(`/companies/${companyId}/inbox/${c.item.id}/credit-note`, { is_credit_note: false });
              const it = { ...c.item, extracted: { ...(c.item.extracted || {}), is_credit_note: false, credit_of_receipt_id: null } };
              await postItem(it, { ...c.override, _creditConfirmed: true });
            } catch (e) { toast.error(e.response?.data?.detail || "Umstellen fehlgeschlagen"); }
          }}
        />
      )}
      {rcDecision && <ReverseChargeDecisionDialog companyId={companyId} item={rcDecision.item} info={rcDecision.info} onCancel={() => setRcDecision(null)}
        onDecide={async (v) => { const { item, override } = rcDecision; setRcDecision(null); await postItem({ ...item, extracted: { ...item.extracted, reverse_charge: v } }, { ...override, reverse_charge: v }); }} />}
      {review && (
        <ReceiptReviewDialog
          companyId={companyId}
          items={reviewLive}
          startIndex={review.startIndex}
          onClose={() => { setReview(null); refresh(); }}
          onPost={postItem}
          onDiscard={(it, onUndo) => discard(it, true, onUndo)}
          onFile={(it, kind) => (kind === "warranty_certificate" ? fileAsWarranty(it) : kind === "receipt_companion" ? fileAsPurchase(it) : kind === "contract" ? fileAsContract(it) : fileAsDocument(it))}
          onUpdated={(id, patch) => setItems((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x)))}
          businessCompanies={businessCompanies}
          isPrivate={company?.company_type === "privat"}
          onCompare={openCompare}
        />
      )}
      <Dialog open={!!previewItem} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className={`${showOriginalEmail ? "max-w-[96vw]" : "max-w-[92vw] xl:max-w-6xl"} max-h-[94vh]`} data-testid="inbox-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-slate-600" />
              {previewItem?.file_name || "Beleg-Vorschau"}
            </DialogTitle>
            <DialogDescription className="sr-only">Vorschau des Belegs mit Download, Seitenwahl und E-Mail-Original</DialogDescription>
          </DialogHeader>
          {previewPages.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="inbox-preview-pages">
              <span className="text-xs text-slate-500 mr-1">{previewPages.length} Seiten:</span>
              {previewPages.map((pg, i) => (
                <button key={pg.file_id || "main"} type="button" onClick={() => showPreviewPage(previewItem, i)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${i === previewPage ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                  title={pg.file_name} data-testid={`inbox-preview-page-${i + 1}`}>
                  Seite {i + 1}{i === 0 ? " (Haupt)" : ""}
                </button>
              ))}
              <div className="flex-1" />
              <span className="text-xs text-slate-500">Reihenfolge:</span>
              <Button variant="outline" size="sm" className="h-7" disabled={previewPage === 0} onClick={() => movePreviewPage(-1)} title={previewPage === 1 ? "Diese Seite wird Seite 1 (Hauptdokument)" : "Diese Seite nach vorne"} data-testid="inbox-preview-move-up"><ChevronUp className="h-3.5 w-3.5 mr-1" /> nach vorne</Button>
              <Button variant="outline" size="sm" className="h-7 mr-3" disabled={previewPage >= previewPages.length - 1} onClick={() => movePreviewPage(1)} title="Diese Seite nach hinten" data-testid="inbox-preview-move-down"><ChevronDown className="h-3.5 w-3.5 mr-1" /> nach hinten</Button>
              <Button variant="outline" size="sm" className="h-7" disabled={previewPage === 0} onClick={() => showPreviewPage(previewItem, previewPage - 1)} data-testid="inbox-preview-prev">‹ Zurück</Button>
              <Button variant="outline" size="sm" className="h-7" disabled={previewPage >= previewPages.length - 1} onClick={() => showPreviewPage(previewItem, previewPage + 1)} data-testid="inbox-preview-next">Weiter ›</Button>
            </div>
          )}
          <div className="w-full h-[76vh] bg-white rounded border border-slate-200 overflow-hidden flex">
            <div className={showOriginalEmail ? "w-1/2 h-full border-r border-slate-200" : "w-full h-full"}>
            {!previewBlobUrl ? (
              <div className="flex items-center justify-center h-full text-sm text-slate-500">
                Lädt Vorschau …
              </div>
            ) : isCsvLike(previewItem?.file_name, previewItem?.content_type) ? (
              <CsvPreview blobUrl={previewBlobUrl} fileName={previewItem?.file_name} />
            ) : (
              <DocumentViewer blobUrl={previewBlobUrl} contentType={previewPages[previewPage]?.content_type || previewItem?.content_type} fileName={previewPages[previewPage]?.file_name || previewItem?.file_name} onSwipe={(dir) => {
                  const n = previewPage + dir;
                  if (n >= 0 && n < previewPages.length) { showPreviewPage(previewItem, n); return; }
                  const list = visibleItems.filter((x) => x.file_name);
                  const i = list.findIndex((x) => x.id === previewItem?.id);
                  const nxt = list[i + dir];
                  if (nxt) openPreview(nxt);
                }} />
            )}
            </div>
            {showOriginalEmail && previewItem?.source_meta?.body_only && (
              <div className="w-1/2 h-full">
                <EmailOriginalPanel companyId={companyId} item={previewItem}
                  onRerendered={async () => { await refresh(); openPreview(previewItem); }} />
              </div>
            )}
          </div>
          <DialogFooter>
            {previewBlobUrl && (
              <div className="mr-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadPreview()} data-testid="inbox-preview-download">
                  <Upload className="h-3.5 w-3.5 mr-1 rotate-180" />
                  Herunterladen
                </Button>
                {previewItem?.source_meta?.body_only && previewItem?.status === "pending" && (
                  <>
                  <Button variant={showOriginalEmail ? "default" : "outline"} size="sm" onClick={() => setShowOriginalEmail((v) => !v)} data-testid="inbox-preview-toggle-original-email"
                    className={showOriginalEmail ? "bg-slate-800 hover:bg-slate-900" : ""} title="Original-E-Mail neben dem PDF anzeigen – zum Vergleichen und Zuschneiden">
                    <Mail className="h-3.5 w-3.5 mr-1" />
                    {showOriginalEmail ? "Original ausblenden" : "Original-E-Mail anzeigen"}
                  </Button>
                  <Button variant="outline" size="sm" disabled={rerenderingId === previewItem.id} onClick={() => rerenderEmail(previewItem)} data-testid="inbox-preview-rerender-email"
                    className="border-sky-300 text-sky-800 hover:bg-sky-50" title="Original-HTML-Layout der E-Mail neu als PDF erzeugen">
                    {rerenderingId === previewItem.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
                    Original-Layout neu erzeugen
                  </Button>
                  </>
                )}
                {(previewItem?.content_type || "").startsWith("image/") && !previewItem?.attachment_preview && (
                  <>
                    <Button variant="outline" size="sm" disabled={rotating} onClick={() => rotatePreview(90)} data-testid="inbox-preview-rotate-left" title="90° nach links drehen">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />
                      Links
                    </Button>
                    <Button variant="outline" size="sm" disabled={rotating} onClick={() => rotatePreview(270)} data-testid="inbox-preview-rotate-right" title="90° nach rechts drehen">
                      <RotateCw className="h-3.5 w-3.5 mr-1" />
                      Rechts
                    </Button>
                    {previewItem?.auto_cropped && (
                      <Button variant="outline" size="sm" disabled={rotating} onClick={uncropPreview} data-testid="inbox-preview-uncrop" title="Automatischen Zuschnitt rückgängig machen">
                        <Crop className="h-3.5 w-3.5 mr-1" />
                        Original wiederherstellen
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            <Button onClick={closePreview}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter269: Konto-Anpassung (Stift) */}
      <Dialog open={!!accountEditItem} onOpenChange={(v) => !v && setAccountEditItem(null)}>
        <DialogContent className="max-w-md" data-testid="inbox-account-dialog">
          <DialogHeader>
            <DialogTitle>Konto wählen</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Auf dieses Konto wird der Aufwand beim Bestätigen gebucht.
          </p>
          <label
            className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
            title="Merkt sich die Gegenpartei — künftige Belege werden automatisch mit diesem Konto vorgeschlagen. Jederzeit in den Buchungsregeln (Bankabgleich) änderbar."
            data-testid="inbox-save-rule-checkbox"
          >
            <input
              type="checkbox"
              checked={saveRuleInbox}
              onChange={(e) => setSaveRuleInbox(e.target.checked)}
              className="accent-slate-900"
            />
            📌 Immer so buchen — Regel für «{accountEditItem?.extracted?.supplier_name || "Gegenpartei"}» speichern
          </label>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 space-y-1" data-testid="inbox-keyword-rule-box">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" title="Legt eine Kontierungsregel an: Belege mit diesen Stichwörtern in Notiz oder Text werden künftig automatisch auf das gewählte Konto kontiert (sicher).">
              <input type="checkbox" checked={keywordRule.enabled} onChange={(e) => setKeywordRule({ ...keywordRule, enabled: e.target.checked })} className="accent-slate-900" data-testid="inbox-keyword-rule-checkbox" />
              🧠 Stichwortregel daraus lernen (Notiz/Belegtext → Konto)
            </label>
            {keywordRule.enabled && (
              <Input value={keywordRule.keywords} onChange={(e) => setKeywordRule({ ...keywordRule, keywords: e.target.value })} placeholder="Stichwörter, kommagetrennt" className="h-7 text-xs" data-testid="inbox-keyword-rule-input" />
            )}
          </div>
          <Input
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            placeholder="Suchen… (z.B. 6600 oder Fahrzeug)"
            data-testid="inbox-account-search"
            autoFocus
          />
          <div className="max-h-72 overflow-y-auto space-y-1">
            {accounts
              .filter((a) => `${a.code} ${a.name} ${a.description || ""}`.toLowerCase().includes(accountFilter.toLowerCase()))
              .slice(0, 60)
              .map((a) => (
                <button
                  key={a.id || a.code}
                  onClick={() => saveAccount(a)}
                  className="w-full text-left text-sm rounded-md border border-slate-200 px-2.5 py-1.5 hover:bg-violet-50 hover:border-violet-300 transition-colors"
                  data-testid={`inbox-account-option-${a.code}`}
                >
                  <span className="font-mono text-slate-700">{a.code}</span>{" "}
                  <span className="text-slate-600">{a.name}</span>
                  {a.description && (
                    <div className="text-[10px] text-slate-400 truncate">{a.description}</div>
                  )}
                </button>
              ))}
            {accounts.length === 0 && (
              <div className="text-xs text-slate-400 py-4 text-center">Lade Kontenplan…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter263/325: Duplikat-Vergleich Seite an Seite (gemeinsame Vergleichs-Lupe) */}
      <DocCompareDialog
        open={!!compare}
        onClose={closeCompare}
        title={compare?.confirm ? "Doppelrechnung? Beide Belege im Vergleich – gleich oder nicht gleich?" : "Duplikat-Prüfung — Duplikat oder zusammengehörige Seiten?"}
        facts={compareFacts(compare)}
        docs={compare ? [
          { id: compare.it.id, title: compare.it.file_name, fetchUrl: `/companies/${companyId}/inbox/${compare.it.id}/file`, fileName: compare.it.file_name, mime: compare.it.content_type,
            tag: "Neues Dokument (dieses)", subtitle: compare.it.kind === "bill" ? "Eingangsrechnung" : (compare.it.kind === "warranty_certificate" || compare.it.warranty?.certificate_only) ? "Garantieschein" : "Beleg/Quittung" },
          ...compare.others.flatMap((o) => [
            { id: o.id, title: o.file_name, fetchUrl: `/companies/${companyId}/inbox/${o.id}/file`, fileName: o.file_name, accent: true,
              tag: compare.it.duplicate_warning?.already_booked ? `Bereits verbucht${o.confirmed_at ? ` am ${formatDateTime(o.confirmed_at)}` : ""} · Seite 1` : "Gegenstück im Posteingang",
              subtitle: o.kind === "bill" ? "Eingangsrechnung" : "Beleg/Quittung" },
            ...(o.attachments || []).slice(0, 2).map((a, i) => ({ id: a.file_id, title: a.file_name, fetchUrl: `/companies/${companyId}/inbox/${o.id}/file?attachment_id=${a.file_id}`, fileName: a.file_name, mime: a.content_type, accent: true,
              tag: `Bereits verbucht · Seite ${i + 2}`, subtitle: "Beilage des verbuchten Belegs" })),
          ]),
        ] : []}
        footer={compare && (
          <div className="flex w-full flex-wrap items-center gap-2">
            {compare.confirm ? (
              <>
                <Button variant="ghost" onClick={closeCompare} data-testid="compare-cancel-btn">Abbrechen</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmCompareNotEqual} data-testid="compare-confirm-post-btn">
                  <Check className="h-4 w-4 mr-1.5" /> Nicht gleich — jetzt bestätigen
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={closeCompare} data-testid="compare-keep-btn">Kein Duplikat — getrennt behalten</Button>
            )}
            <div className="flex-1" />
            {(compare.it.kind === "warranty_certificate" || compare.it.warranty?.certificate_only) && compare.it.duplicate_warning?.already_booked && (
              <Button className="bg-violet-700 hover:bg-violet-800" onClick={async () => { const c = compare; closeCompare(); await fileAsWarranty(c.it); }} data-testid="compare-file-warranty-btn" title="Kein Duplikat: Garantieschein zum bereits gebuchten Kaufbeleg – wird als Garantie abgelegt und mit dem Beleg verknüpft, nichts wird gebucht">
                <Shield className="h-4 w-4 mr-1.5" /> Garantieschein zum Kaufbeleg — als Garantie ablegen & verknüpfen
              </Button>
            )}
            {!compare.it.duplicate_warning?.already_booked ? (
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={mergeCompare} data-testid="compare-merge-btn">
                <Layers className="h-4 w-4 mr-1.5" /> Gehören zusammen (Seite 1+2) — zusammenführen
              </Button>
            ) : (
              <>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => attachToBooked("before")} title="Dieses Blatt wird Seite 1 des verbuchten Belegs" data-testid="compare-attach-before-btn">
                  <Layers className="h-4 w-4 mr-1.5" /> Gehört zum verbuchten Beleg — als Seite 1 (vorne)
                </Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => attachToBooked("after")} title="Dieses Blatt wird die letzte Seite des verbuchten Belegs" data-testid="compare-attach-after-btn">
                  <Layers className="h-4 w-4 mr-1.5" /> … als letzte Seite (hinten)
                </Button>
              </>
            )}
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => { const itm = compare.it; closeCompare(); discard(itm, true); }} data-testid="compare-discard-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> {compare.confirm ? "Gleich — dieses verwerfen" : "Ist ein Duplikat — dieses verwerfen"}
            </Button>
          </div>
        )}
      />
    </div>
  );
}


function EditInboxDialog({ item, onClose, onPost }) {
  const ex = item.extracted || {};
  const [form, setForm] = useState({
    kind: item.kind,
    invoice_number: ex.invoice_number || "",
    invoice_date: ex.invoice_date || "",
    due_date: ex.due_date || "",
    paid_date: ex.paid_date || "",
    total_amount: ex.total_amount || 0,
    tax_rate: ex.tax_rate || 0.081,
    account_code: ex.suggested_account || "",
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="inbox-edit-dialog">
        <DialogHeader>
          <DialogTitle>Vor dem Buchen prüfen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Art</Label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full mt-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
                data-testid="inbox-edit-kind"
              >
                <option value="bill">Eingangsrechnung</option>
                <option value="receipt">Beleg/Quittung</option>
              </select>
            </div>
            <div>
              <Label>Lieferant</Label>
              <Input value={ex.supplier_name || ""} readOnly className="bg-slate-50" />
            </div>
            <div>
              <Label>Datum</Label>
              <SwissDateField
                
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                data-testid="inbox-edit-date"
              />
            </div>
            {form.kind === "bill" && (
              <div>
                <Label>Fällig am</Label>
                <SwissDateField
                  
                  value={form.due_date || ""}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="inbox-edit-due"
                />
              </div>
            )}
            <div>
              <Label>Bezahlt/belastet am (optional)</Label>
              <SwissDateField
                
                value={form.paid_date || ""}
                onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                data-testid="inbox-edit-paid-date"
              />
            </div>
            <div>
              <Label>Betrag (CHF)</Label>
              <Input
                type="number" step="0.01"
                value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })}
                data-testid="inbox-edit-amount"
              />
            </div>
            <div>
              <Label>MWST-Satz</Label>
              <select
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) })}
                className="w-full mt-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
              >
                <option value={0.081}>8.1% (Normal)</option>
                <option value={0.026}>2.6% (Reduziert)</option>
                <option value={0.038}>3.8% (Beherbergung)</option>
                <option value={0}>0% (Befreit)</option>
              </select>
            </div>
            {form.kind === "bill" && (
              <div className="col-span-2">
                <Label>Rechnungs-Nr. (vom Lieferanten)</Label>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="z.B. R-2026-1234"
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={() => onPost(form)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="inbox-edit-submit"
          >
            <Check className="h-4 w-4 mr-1" />
            Bestätigen & Buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
